import { type Layer, ObjectCategory, ZIndexes } from "@common/constants";
import { type MeleeDefinition } from "@common/definitions/items/melees";
import { DamageSources, type KillData } from "@common/packets/killPacket";
import { FloorNames } from "@common/utils/terrain";
import { Vec, type Vector } from "@common/utils/vector";
import { CanvasSource, Container, Graphics, Rectangle, Sprite, Texture } from "pixi.js";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import type { DeathMarker } from "../objects/deathMarker";
import type { Player } from "../objects/player";
import { type GearLook, PlayerGear } from "../objects/playerGear";
import { PIXI_SCALE } from "../utils/constants";
import { TrenchioSprite } from "../utils/pixi";
import { WarLook } from "../war/warLook";
import { CameraManager } from "./cameraManager";
import { MapManager } from "./mapManager";

/*
 * Blood and gore (Settings → Graphics → Blood & gore: Off, Normal or Extreme, Extreme by default).
 *
 * Everything here is drawn on this computer from what the game already tells it (bullet hits, melee hits, deaths
 * and their causes in the kill packets, explosions): nothing is sent, and the server never knows. Each player picks
 * their own level.
 *
 * - Normal: hits spray blood out of the far side of the body (more for bigger guns) and leave stains that dry from
 *   bright red to brown over about a minute; blood soaks into the uniform; the dead stay where they fell, in a pool
 *   that spreads under them, and look dead, not like a player standing still: they fall limp (one arm out to the
 *   side, the other across the body, flat on the ground), the colour drains out of them to an ashen grey, the bullets
 *   that hit them leave holes, and flies come to them. Their skull marker hides while the body lies over it, and
 *   their helmet drops as loot (the server's), so no other is drawn.
 * - Extreme: all of that, and a killing shot can tear an arm off (the stump spurts), explosions blow bodies apart
 *   into arms, flesh and bone, blades leave cuts, the chainsaw splits a body in two, and big hits throw flesh and bone.
 * - Off: a hit raises a puff of dust and the dead vanish under a skull, as the game always did.
 *
 * Bodies, body parts and stains stay 4 minutes, then fade away. There are caps on how many of each are kept (fewer
 * on a computer that runs the lighter Battlefield look), and the oldest go first.
 *
 * Units: positions are world pixels (game units × PIXI_SCALE) and the body is drawn in sprite pixels, a player
 * 45 px in radius. The look was designed on smaller players (30 px), so the designed sizes and speeds are scaled by M.
 */

export type GoreLevel = "off" | "normal" | "extreme";

/** what hit a player, as far as this computer can tell */
export type GoreHit =
    | { readonly kind: "bullet", readonly direction: Vector, readonly damage: number }
    | { readonly kind: "melee", readonly weapon: MeleeDefinition };

const TAU = Math.PI * 2;
/** the suggestions' sizes and speeds (players 30 px in radius) → the game's (45 px) */
const M = 1.5;
/** a player's radius in sprite pixels */
const RADIUS = 45;
const GRAVITY = 900 * M;
/** how long bodies, parts and stains stay, then how long they take to fade */
const LIFETIME = 240_000;
const FADE = 4_000;
/** the oldest go quickly when a cap is reached */
const QUICK_FADE = 600;
/** blood soaked into a living player's uniform */
const SPOT_LIFETIME = 30_000;
const MAX_SPOTS = 7;
/** seconds for spilt blood to dry from wet red to brown */
const DRY_SECONDS = 60;
/** how long a death waits for its kill packet (it comes in the same message, so this is only a fallback) */
const KILL_WAIT = 150;
/** blood in the air at once, at most */
const MAX_DROPS = 900;
/**
 * How a body lies: where its hands come to rest, in its own frame (x the way it faced, y to its right). One arm has
 * fallen out to the side, an arm's length from its shoulder (as the sleeves are drawn: about as far as a living hand
 * reaches, no further), the other across the body; the hands lie loose (a little smaller than a living grip). Mirrored
 * at random, and the arm out swung a little one way or the other each time.
 */
const LIMP_SHOULDER = Vec(6, -34);
const LIMP_REACH = 34;
const LIMP_ANGLE = -1.15;
const LIMP_IN = Vec(6, 26);
const LIMP_HAND = 0.85;
/** how long a body takes to fall limp, and for the colour to drain out of it (and the blood to soak out round its holes) */
const SETTLE = 450;
const DRAIN = 1600;
/** a body's colour: ashen, a little blue (no life in it); and a burnt one */
const ASHEN = 0x939ca2;
const BURNT = 0x6f6a64;
/** bullet holes on a body: where the shots of its last half a minute went in, the latest few */
const WOUND_TIME = 30_000;
const MAX_HOLES = 5;
/** a fly's size over its drawing */
const FLY_SCALE = 1.5;

type RGB = readonly [number, number, number];
const FRESH: RGB = [158, 16, 24];
const WET: RGB = [108, 10, 16];
const DRY: RGB = [71, 16, 16];
const MEAT = "#8c1a20";
const MEAT_HI = "#c2463d";
const BONE = "#ece4d1";
const BONE_LINE = "#a89d86";
const MIST_TINT = 0xaf161e;
const DUST_TINT = 0x9b8f77;
const SHADOW_TINT = 0x191c0e;
const SHINE_TINT = 0xffbebe;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const between = (a: number, b: number): number => a + Math.random() * (b - a);
const sign = (): 1 | -1 => (Math.random() < 0.5 ? -1 : 1);
const pick = <T>(list: readonly T[]): T => list[Math.floor(Math.random() * list.length)];
const toRGB = (color: number): RGB => [(color >> 16) & 255, (color >> 8) & 255, color & 255];
const mix = (a: RGB, b: RGB, t: number): number => {
    const k = clamp01(t);
    return (Math.round(a[0] + (b[0] - a[0]) * k) << 16) | (Math.round(a[1] + (b[1] - a[1]) * k) << 8) | Math.round(a[2] + (b[2] - a[2]) * k);
};
const hex = (color: number): string => `#${color.toString(16).padStart(6, "0")}`;
const WHITE: RGB = [255, 255, 255];
/** spilt blood's colour `age` seconds on: bright, then wet, then dried brown */
const bloodAt = (age: number): number => (age < 1.5 ? mix(FRESH, WET, age / 1.5) : mix(WET, DRY, (age - 1.5) / DRY_SECONDS));
/** the colour changes in steps (a quarter second while fresh, then 2.5 s), so a stain's tint is set ~30 times, not every frame */
const dryStep = (age: number): number => (age < 1.5 ? Math.floor(age * 4) : 6 + Math.floor((age - 1.5) / 2.5));
const FRESH_TINT = (FRESH[0] << 16) | (FRESH[1] << 8) | FRESH[2];

type Rand = () => number;
const mulberry32 = (seed: number): Rand => (): number => {
    let t = (seed = (seed + 0x6D2B79F5) | 0);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

// ----------------------------------------------------------------------------------------------------------- shapes

interface Lobe { readonly dx: number, readonly dy: number, readonly r: number }

/** a blot: a round middle, lobes around it (`even`: spread all the way round), and a few droplets thrown clear */
function makeLobes(rand: Rand, r: number, lobes: number, droplets: number, even = false): Lobe[] {
    const out: Lobe[] = [{ dx: 0, dy: 0, r }];
    for (let i = 0; i < lobes; i++) {
        const a = even ? ((i + rand() * 0.7) / lobes) * TAU : rand() * TAU, d = r * (0.45 + rand() * 0.55);
        out.push({ dx: Math.cos(a) * d, dy: Math.sin(a) * d, r: r * (0.32 + rand() * 0.4) });
    }
    for (let i = 0; i < droplets; i++) {
        const a = rand() * TAU, d = r * (1.3 + rand() * 0.9);
        out.push({ dx: Math.cos(a) * d, dy: Math.sin(a) * d, r: r * (0.1 + rand() * 0.14) });
    }
    return out;
}

function fillLobes(ctx: CanvasRenderingContext2D, cx: number, cy: number, lobes: readonly Lobe[]): void {
    ctx.beginPath();
    for (const l of lobes) {
        ctx.moveTo(cx + l.dx + l.r, cy + l.dy);
        ctx.arc(cx + l.dx, cy + l.dy, l.r, 0, TAU);
    }
    ctx.fill();
}

function context(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Blood & gore: no 2D canvas");
    return ctx;
}

/** the sizes each shape is drawn at (texture pixels): a sprite's scale is the size wanted over these */
const DOT_R = 30;
const MIST_R = 32;
const DROP_R = 12;
const DROP_W = 120;
const DROP_CX = 40;
const DROP_STRETCH = [1, 1.6, 2.3, 3.1] as const;
const SPLAT_R = 20;
const POOL_R = 100;
const SPOT_R = 6;
const BONE_LEN = 12;
const CHUNK_SIZE = 20;

interface GoreAtlas {
    /** white: drops in the air, shadows, the pool's shine */
    readonly dot: Texture
    /** white, soft-edged: a fine spray of blood, or dust */
    readonly mist: Texture
    /** white stains stretched the way the drop flew: [stretch][0 clean, 1 and 2 with a droplet ahead] */
    readonly drops: ReadonlyArray<readonly Texture[]>
    /** white splats */
    readonly splats: readonly Texture[]
    /** white pools */
    readonly pools: readonly Texture[]
    /** white blots soaked into a uniform */
    readonly spots: readonly Texture[]
    /** cuts across a body (drawn, inside its circle) */
    readonly slashes: readonly Texture[]
    readonly deepSlash: Texture
    /** where an arm was */
    readonly stump: Texture
    readonly bone: Texture
}

let atlas: GoreAtlas | undefined;

function buildAtlas(): GoreAtlas {
    const W = 1024, H = 1024;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = context(canvas);
    const rand = mulberry32(0x60e);
    const source = new CanvasSource({ resource: canvas, scaleMode: "linear", autoGenerateMipmaps: true });

    // cells packed in rows, 2 px apart (so mipmaps don't bleed between shapes)
    let x = 0, y = 0, rowH = 0;
    const cell = (w: number, h: number): Rectangle => {
        if (x + w > W) {
            x = 0;
            y += rowH + 2;
            rowH = 0;
        }
        const rect = new Rectangle(x, y, w, h);
        x += w + 2;
        rowH = Math.max(rowH, h);
        return rect;
    };
    const texture = (frame: Rectangle): Texture => new Texture({ source, frame });

    ctx.fillStyle = "#fff";
    const pools: Texture[] = [];
    for (let i = 0; i < 3; i++) {
        const c = cell(220, 220);
        fillLobes(ctx, c.x + 110, c.y + 110, makeLobes(rand, POOL_R * 0.62, 7, 0, true));
        pools.push(texture(c));
    }
    const splats: Texture[] = [];
    for (let i = 0; i < 6; i++) {
        const c = cell(100, 100);
        fillLobes(ctx, c.x + 50, c.y + 50, makeLobes(rand, SPLAT_R, 5, 3));
        splats.push(texture(c));
    }

    // cuts: a wet stroke with raw flesh in it, kept inside the body's circle
    const slash = (k: number): Texture => {
        const c = cell(96, 96);
        const cx = c.x + 48, cy = c.y + 48;
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, 44.5, 0, TAU);
        ctx.clip();
        const x0 = cx - 30 * k, y0 = cy - 34 + (rand() - 0.5) * 6, x1 = cx + 26 * k, y1 = cy + 30 + (rand() - 0.5) * 6;
        const qx = cx + 10 + (rand() - 0.5) * 8, qy = cy - 6 + (rand() - 0.5) * 8;
        ctx.lineCap = "round";
        ctx.strokeStyle = hex(mix(WET, FRESH, 0.35));
        ctx.lineWidth = 6 * Math.min(1.2, k);
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.quadraticCurveTo(qx, qy, x1, y1); ctx.stroke();
        ctx.strokeStyle = MEAT_HI;
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.quadraticCurveTo(qx, qy, x1, y1); ctx.stroke();
        // a few drips running off the cut
        ctx.fillStyle = hex(mix(WET, FRESH, 0.35));
        for (let i = 0; i < 3; i++) {
            const t = 0.2 + rand() * 0.6, u = 1 - t;
            const px = u * u * x0 + 2 * u * t * qx + t * t * x1, py = u * u * y0 + 2 * u * t * qy + t * t * y1;
            ctx.beginPath(); ctx.ellipse(px + 2, py + 5, 2.2, 4 + rand() * 3, 0.3, 0, TAU); ctx.fill();
        }
        ctx.restore();
        ctx.fillStyle = "#fff";
        return texture(c);
    };
    const slashes = [slash(0.9), slash(1)];
    const deepSlash = slash(1.25);

    const drops: Texture[][] = [];
    for (const stretch of DROP_STRETCH) {
        const variants: Texture[] = [];
        for (let v = 0; v < 3; v++) {
            const c = cell(DROP_W, 32);
            const cx = c.x + DROP_CX, cy = c.y + 16;
            ctx.beginPath();
            ctx.ellipse(cx, cy, DROP_R * stretch, DROP_R, 0, 0, TAU);
            if (v > 0) {
                const d = DROP_R * (stretch + 1.2 + rand() * 1.5), s = DROP_R * (0.25 + rand() * 0.25);
                ctx.moveTo(cx + d + s, cy);
                ctx.arc(cx + d, cy, s, 0, TAU);
            }
            ctx.fill();
            variants.push(texture(c));
        }
        drops.push(variants);
    }

    const dotCell = cell(64, 64);
    ctx.beginPath();
    ctx.arc(dotCell.x + 32, dotCell.y + 32, DOT_R, 0, TAU);
    ctx.fill();

    const mistCell = cell(64, 64);
    const gradient = ctx.createRadialGradient(mistCell.x + 32, mistCell.y + 32, 0, mistCell.x + 32, mistCell.y + 32, MIST_R);
    gradient.addColorStop(0, "rgba(255,255,255,1)");
    gradient.addColorStop(0.45, "rgba(255,255,255,0.55)");
    gradient.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(mistCell.x, mistCell.y, 64, 64);
    ctx.fillStyle = "#fff";

    const spots: Texture[] = [];
    for (let i = 0; i < 6; i++) {
        const c = cell(24, 24);
        fillLobes(ctx, c.x + 12, c.y + 12, makeLobes(rand, SPOT_R, 4, 0));
        spots.push(texture(c));
    }

    const stumpCell = cell(40, 40);
    {
        const cx = stumpCell.x + 20, cy = stumpCell.y + 20;
        ctx.fillStyle = MEAT; ctx.beginPath(); ctx.arc(cx, cy, 15, 0, TAU); ctx.fill();
        ctx.fillStyle = MEAT_HI; ctx.beginPath(); ctx.arc(cx - 2, cy - 2, 7, 0, TAU); ctx.fill();
        ctx.fillStyle = BONE; ctx.beginPath(); ctx.arc(cx, cy, 4.5, 0, TAU); ctx.fill();
    }

    const boneCell = cell(32, 14);
    {
        const cx = boneCell.x + 16, cy = boneCell.y + 7;
        ctx.fillStyle = BONE; ctx.strokeStyle = BONE_LINE; ctx.lineWidth = 0.8;
        ctx.beginPath(); ctx.ellipse(cx, cy, BONE_LEN, BONE_LEN * 0.32, 0, 0, TAU); ctx.fill(); ctx.stroke();
        // knuckle ends
        ctx.beginPath(); ctx.arc(cx - BONE_LEN + 1.5, cy - 1.6, 2, 0, TAU); ctx.arc(cx + BONE_LEN - 1.5, cy + 1.6, 2, 0, TAU); ctx.fill();
    }

    source.update();
    return {
        dot: texture(dotCell),
        mist: texture(mistCell),
        drops,
        splats,
        pools,
        spots,
        slashes,
        deepSlash,
        stump: texture(stumpCell),
        bone: texture(boneCell)
    };
}

function shapes(): GoreAtlas {
    return atlas ??= buildAtlas();
}

/** the bullet holes' and flies' drawings: a hole (cell centred on it), the blood soaked round one (the hole at SOAK_CX) */
const HOLE_CELL = 18;
const SOAK_W = 48;
const SOAK_H = 30;
const SOAK_CX = 15;
const FLY_CELL = 22;
/** they are small and seen close: drawn at 3× */
const BODY_RES = 3;

interface BodyAtlas {
    /** bullet holes: dark, deepest in the middle, in the wet, torn edge of the cloth, a glint of wet on the rim */
    readonly holes: readonly Texture[]
    /** the blood soaked into the cloth round a hole; most run on toward the ground (along +x) */
    readonly soaks: readonly Texture[]
    /** a fly in the air (two beats of its wings, facing +x), and one sitting, its wings folded */
    readonly flying: readonly [Texture, Texture]
    readonly sitting: Texture
}

let bodyAtlas: BodyAtlas | undefined;

/** a ragged closed outline round (cx, cy): `n` points at radius r, give or take `jitter` of it */
function ragged(ctx: CanvasRenderingContext2D, rand: Rand, cx: number, cy: number, n: number, r: number, jitter: number): void {
    ctx.beginPath();
    for (let i = 0; i < n; i++) {
        const a = (i / n) * TAU + (rand() - 0.5) * (TAU / n) * 0.5, d = r * (1 - jitter + rand() * jitter * 2);
        const x = cx + Math.cos(a) * d, y = cy + Math.sin(a) * d;
        if (i) ctx.lineTo(x, y);
        else ctx.moveTo(x, y);
    }
    ctx.closePath();
}

/** a soft blot of blood, fading out at its edge (blots laid over each other build up darker) */
function blot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rgb: string, alpha: number): void {
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, r);
    gradient.addColorStop(0, `rgba(${rgb}, ${alpha})`);
    gradient.addColorStop(0.62, `rgba(${rgb}, ${alpha * 0.72})`);
    gradient.addColorStop(1, `rgba(${rgb}, 0)`);
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fill();
}

function buildBodyAtlas(): BodyAtlas {
    const W = 256, H = 128;
    const canvas = document.createElement("canvas");
    canvas.width = W * BODY_RES;
    canvas.height = H * BODY_RES;
    const ctx = context(canvas);
    ctx.scale(BODY_RES, BODY_RES);
    const rand = mulberry32(0xb0d1);
    const source = new CanvasSource({ resource: canvas, resolution: BODY_RES, scaleMode: "linear", autoGenerateMipmaps: true });

    let x = 0, y = 0, rowH = 0;
    const cell = (w: number, h: number): Rectangle => {
        if (x + w > W) {
            x = 0;
            y += rowH + 2;
            rowH = 0;
        }
        const rect = new Rectangle(x, y, w, h);
        x += w + 2;
        rowH = Math.max(rowH, h);
        return rect;
    };
    const texture = (frame: Rectangle): Texture => new Texture({ source, frame });

    // the colours are meant for a body's ashen tint, which takes them about 40% darker
    const holes: Texture[] = [];
    for (let i = 0; i < 4; i++) {
        const c = cell(HOLE_CELL, HOLE_CELL);
        const cx = c.x + HOLE_CELL / 2, cy = c.y + HOLE_CELL / 2;
        // the wet edge of the torn cloth: a dark ring, a little ragged
        ctx.fillStyle = "#43070b";
        ragged(ctx, rand, cx, cy, 13, 3.75, 0.12);
        ctx.fill();
        // the hole
        const deep = ctx.createRadialGradient(cx + 0.3, cy + 0.3, 0, cx, cy, 2.9);
        deep.addColorStop(0, "#000000");
        deep.addColorStop(0.65, "#0a0102");
        deep.addColorStop(1, "#1e0406");
        ctx.fillStyle = deep;
        ragged(ctx, rand, cx, cy, 11, 2.6, 0.14);
        ctx.fill();
        // its far side catches the light (lower right), and the near rim glints wet (upper left)
        ctx.strokeStyle = "rgba(176, 58, 62, 0.55)";
        ctx.lineWidth = 0.55;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.arc(cx, cy, 1.85, 0.2, 1.4);
        ctx.stroke();
        ctx.fillStyle = "rgba(255, 238, 238, 0.6)";
        ctx.beginPath();
        ctx.ellipse(cx - 2.6, cy - 2.5, 1.05, 0.5, -0.75, 0, TAU);
        ctx.fill();
        holes.push(texture(c));
    }

    const BLOOD = "150, 14, 24", DEEP = "96, 6, 12";
    const soaks: Texture[] = [];
    for (const run of [0, 11, 14, 17, 20, 0]) {
        const c = cell(SOAK_W, SOAK_H);
        const cx = c.x + SOAK_CX, cy = c.y + SOAK_H / 2;
        // blood soaked into the cloth round the hole
        for (const l of makeLobes(rand, 4.4, 7, 0, true)) blot(ctx, cx + l.dx, cy + l.dy, l.r * 1.7, BLOOD, 0.62);
        // soaking on from it toward the ground (the body lies on its back): a tapering tongue, a drop gathering at its end
        if (run > 0) {
            const bend = (rand() - 0.5) * 5, x1 = cx + run, y1 = cy + bend * 0.6, qx = cx + run * 0.5, qy = cy + bend;
            const steps = Math.ceil(run / 1.2);
            for (let i = 1; i <= steps; i++) {
                const t = i / steps, u = 1 - t;
                const px = u * u * cx + 2 * u * t * qx + t * t * x1, py = u * u * cy + 2 * u * t * qy + t * t * y1;
                blot(ctx, px, py + (rand() - 0.5) * 0.6, 4.6 * u + 1.9 * t, BLOOD, 0.42);
            }
            blot(ctx, x1 + 0.4, y1, 2.3, DEEP, 0.55);
        }
        // a few flecks thrown back out of it
        for (let i = 0; i < 3; i++) {
            const a = (rand() - 0.5) * 2.6, d = 7 + rand() * 3;
            blot(ctx, cx + Math.cos(a) * d, cy + Math.sin(a) * d, 0.9 + rand() * 0.7, BLOOD, 0.8);
        }
        // darkest round the hole, and wet: a soft shine
        blot(ctx, cx, cy, 6, DEEP, 0.9);
        ctx.fillStyle = "rgba(255, 226, 226, 0.16)";
        ctx.beginPath();
        ctx.ellipse(cx - 3.6, cy - 3.8, 2.7, 1, -0.7, 0, TAU);
        ctx.fill();
        soaks.push(texture(c));
    }

    // a fly, facing +x: wings (under), abdomen, thorax, head and its red eyes
    const fly = (wings: "up" | "down" | "folded"): Texture => {
        const c = cell(FLY_CELL, FLY_CELL);
        const cx = c.x + FLY_CELL / 2, cy = c.y + FLY_CELL / 2;
        const spread = wings === "folded" ? 0.16 : wings === "up" ? 1.05 : 0.6;
        const length = wings === "folded" ? 5.6 : 6.6;
        ctx.fillStyle = wings === "folded" ? "rgba(205, 214, 222, 0.55)" : wings === "up" ? "rgba(228, 234, 242, 0.6)" : "rgba(228, 234, 242, 0.34)";
        for (const side of [-1, 1]) {
            const a = Math.PI - side * spread;
            ctx.beginPath();
            ctx.ellipse(cx - 0.4 + Math.cos(a) * length * 0.5, cy + Math.sin(a) * length * 0.5, length * 0.5, 2, a, 0, TAU);
            ctx.fill();
        }
        ctx.fillStyle = "#101010";
        ctx.beginPath();
        ctx.ellipse(cx - 1.8, cy, 3.3, 2.2, 0, 0, TAU);
        ctx.fill();
        ctx.beginPath();
        ctx.ellipse(cx + 1.3, cy, 2.05, 1.95, 0, 0, TAU);
        ctx.fill();
        ctx.fillStyle = "#1a1212";
        ctx.beginPath();
        ctx.arc(cx + 3.4, cy, 1.45, 0, TAU);
        ctx.fill();
        ctx.fillStyle = "#6a1d16";
        ctx.beginPath();
        ctx.arc(cx + 3.8, cy - 0.95, 0.65, 0, TAU);
        ctx.moveTo(cx + 4.45, cy + 0.95);
        ctx.arc(cx + 3.8, cy + 0.95, 0.65, 0, TAU);
        ctx.fill();
        return texture(c);
    };
    const flying: readonly [Texture, Texture] = [fly("up"), fly("down")];
    const sitting = fly("folded");

    source.update();
    return { holes, soaks, flying, sitting };
}

function bodyShapes(): BodyAtlas {
    return bodyAtlas ??= buildBodyAtlas();
}

/** a severed forearm in a player's own sleeve colours: the cut end at x = -26, the wrist at +11 (the hand is a sprite on top, at +22) */
const ARM_W = 64;
const ARM_CX = 36;
const armTextures = new Map<string, Texture>();
function armTexture(look: GearLook): Texture {
    const key = `${look.sleeve}:${look.edge}:${look.cuff}`;
    let texture = armTextures.get(key);
    if (texture) return texture;
    const canvas = document.createElement("canvas");
    canvas.width = ARM_W * 2;
    canvas.height = 28 * 2;
    const ctx = context(canvas);
    ctx.scale(2, 2);
    ctx.translate(ARM_CX, 14);
    ctx.lineCap = "butt";
    ctx.strokeStyle = hex(look.edge); ctx.lineWidth = 20;
    ctx.beginPath(); ctx.moveTo(-26, 0); ctx.lineTo(11, 0); ctx.stroke();
    ctx.fillStyle = hex(look.edge); ctx.beginPath(); ctx.arc(11, 0, 10, 0, TAU); ctx.fill();
    ctx.strokeStyle = hex(look.sleeve); ctx.lineWidth = 15;
    ctx.beginPath(); ctx.moveTo(-26, 0); ctx.lineTo(11, 0); ctx.stroke();
    ctx.fillStyle = hex(look.sleeve); ctx.beginPath(); ctx.arc(11, 0, 7.5, 0, TAU); ctx.fill();
    ctx.fillStyle = hex(look.cuff); ctx.strokeStyle = hex(look.edge); ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(6, 0, 7.5, 0, TAU); ctx.fill(); ctx.stroke();
    ctx.fillStyle = MEAT; ctx.beginPath(); ctx.ellipse(-26, 0, 6, 10, 0, 0, TAU); ctx.fill();
    ctx.fillStyle = MEAT_HI; ctx.beginPath(); ctx.ellipse(-26, -2, 3, 6, 0, 0, TAU); ctx.fill();
    ctx.fillStyle = BONE; ctx.beginPath(); ctx.arc(-26, 0, 3.6, 0, TAU); ctx.fill();
    texture = new Texture({ source: new CanvasSource({ resource: canvas, scaleMode: "linear", resolution: 2 }) });
    armTextures.set(key, texture);
    return texture;
}

/** lumps of flesh with a scrap of the uniform still on them, in that uniform's colour */
const chunkTextures = new Map<number, readonly Texture[]>();
function chunkTexturesFor(color: number): readonly Texture[] {
    let textures = chunkTextures.get(color);
    if (textures) return textures;
    const canvas = document.createElement("canvas");
    canvas.width = 4 * 52;
    canvas.height = 52;
    const ctx = context(canvas);
    const rand = mulberry32(color ^ 0x5eed);
    const source = new CanvasSource({ resource: canvas, scaleMode: "linear", autoGenerateMipmaps: true });
    const list: Texture[] = [];
    for (let i = 0; i < 4; i++) {
        const cx = i * 52 + 26, cy = 26, size = CHUNK_SIZE;
        const pts: Array<readonly [number, number]> = [];
        for (let k = 0; k < 6; k++) {
            const a = (k / 6) * TAU, d = size * (0.65 + rand() * 0.5);
            pts.push([Math.cos(a) * d, Math.sin(a) * d]);
        }
        const poly = (scale: number, shift: number): void => {
            ctx.beginPath();
            pts.forEach(([px, py], k) => (k ? ctx.lineTo(cx + px * scale + shift, cy + py * scale) : ctx.moveTo(cx + px * scale + shift, cy + py * scale)));
            ctx.closePath();
            ctx.fill();
        };
        ctx.fillStyle = MEAT; poly(1, 0);
        ctx.fillStyle = hex(color); poly(0.78, size * 0.22);
        ctx.fillStyle = MEAT_HI; ctx.beginPath(); ctx.arc(cx - size * 0.45, cy, size * 0.22, 0, TAU); ctx.fill();
        list.push(new Texture({ source, frame: new Rectangle(i * 52, 0, 52, 52) }));
    }
    source.update();
    chunkTextures.set(color, textures = list);
    return textures;
}

// ----------------------------------------------------------------------------------------------------------- state

/** caps by the Battlefield look's level (`flies`: on each body) */
interface Tier { readonly corpses: number, readonly stains: number, readonly parts: number, readonly drops: number, readonly flies: number }
const TIERS: Readonly<Record<"high" | "medium" | "low", Tier>> = {
    high: { corpses: 30, stains: 700, parts: 160, drops: 1, flies: 4 },
    medium: { corpses: 20, stains: 450, parts: 100, drops: 0.75, flies: 3 },
    low: { corpses: 12, stains: 250, parts: 60, drops: 0.5, flies: 2 }
};

/** the blood a hit throws, by the damage of one bullet: a pistol's, a rifle's and a sniper rifle's (as suggested) */
interface Power { readonly n: number, readonly sMax: number, readonly rMax: number, readonly mist: number, readonly splat: number }
const POWER: ReadonlyArray<Power & { readonly damage: number }> = [
    { damage: 2, n: 1, sMax: 140, rMax: 1.9, mist: 8, splat: 2.4 },
    { damage: 12, n: 5, sMax: 210, rMax: 2.6, mist: 16, splat: 4 },
    { damage: 24, n: 10, sMax: 320, rMax: 3.5, mist: 25, splat: 5.5 },
    { damage: 60, n: 18, sMax: 430, rMax: 4.8, mist: 38, splat: 8 }
];
function powerOf(damage: number): Power {
    const first = POWER[0], last = POWER[POWER.length - 1];
    if (damage <= first.damage) return first;
    if (damage >= last.damage) return last;
    let lo = first;
    for (const hi of POWER) {
        if (damage <= hi.damage) {
            const t = (damage - lo.damage) / (hi.damage - lo.damage);
            const lerp = (a: number, b: number): number => a + (b - a) * t;
            return { n: lerp(lo.n, hi.n), sMax: lerp(lo.sMax, hi.sMax), rMax: lerp(lo.rMax, hi.rMax), mist: lerp(lo.mist, hi.mist), splat: lerp(lo.splat, hi.splat) };
        }
        lo = hi;
    }
    return last;
}
/** Extreme throws more, faster and in bigger drops */
const EXTREME = { n: 1.6, s: 1.15, r: 1.25, mist: 1.3, splat: 1.25 } as const;
const NORMAL = { n: 1, s: 1, r: 1, mist: 1, splat: 1 } as const;

/** edged weapons cut (a match on the weapon's id) */
const SHARP = /knife|kbar|kukri|seax|falchion|sword|machete|katana|axe|hatchet|sickle|scythe|cleaver|blade|bayonet|sabre|saber|dagger|fang|pick|spade|shovel|entrenching|karambit|bowie|glaive|halberd|spear/;
type MeleeKind = "chainsaw" | "sharp" | "blunt";
function meleeKind(weapon?: { readonly idString: string }): MeleeKind {
    const id = weapon?.idString ?? "";
    if (id.includes("chainsaw") || id.includes("chain_saw")) return "chainsaw";
    return SHARP.test(id) ? "sharp" : "blunt";
}

interface Roots {
    readonly layer: Layer
    readonly pools: Container
    readonly stains: Container
    /** bodies, and parts lying on the ground (under loot and obstacles) */
    readonly ground: Container
    /** anything in the air (over players) */
    readonly air: Container
    /** stains, in squares of the map that are shown only near the camera */
    readonly chunks: Map<number, Container>
}
const CHUNK = 1024;
const chunkKey = (x: number, y: number): number => Math.max(0, Math.floor(x / CHUNK)) * 4096 + Math.max(0, Math.floor(y / CHUNK));

interface Stain {
    readonly sprite: Sprite
    readonly born: number
    readonly alpha: number
    fadeAt: number
    fadeFor: number
    step: number
    fading: boolean
    dead: boolean
}

interface Drop {
    x: number
    y: number
    z: number
    vx: number
    vy: number
    vz: number
    readonly rad: number
    readonly layer: Layer
    readonly wet: boolean
    readonly sprite: Sprite
    readonly shadow: Sprite
}

interface Mist {
    readonly sprite: Sprite
    readonly born: number
    readonly life: number
    readonly r0: number
    readonly r1: number
    readonly alpha: number
    readonly vx: number
    readonly vy: number
    x: number
    y: number
}

interface Part {
    readonly view: Container
    readonly shadow: Sprite
    readonly layer: Layer
    readonly scale: number
    readonly friction: number
    readonly bleed: number
    readonly shadowW: number
    readonly shadowH: number
    x: number
    y: number
    z: number
    vx: number
    vy: number
    vz: number
    rot: number
    vrot: number
    trail: number
    grounded: boolean
    resting: boolean
    fadeAt: number
    fadeFor: number
    fading: boolean
    dead: boolean
}

interface Emitter {
    pulses: number
    next: number
    readonly every: number
    readonly n: number
    readonly sMin: number
    readonly sMax: number
    readonly layer: Layer
    readonly wet: boolean
    readonly origin: () => { readonly x: number, readonly y: number, readonly ang: number } | undefined
}

interface Pool {
    readonly sprite: Sprite
    readonly shine: Sprite
    readonly born: number
    readonly grow: number
    readonly maxR: number
    readonly x: number
    readonly y: number
    step: number
}

/** a fly over a body: it comes, buzzes round, and now and then sits on the body or the blood by it (body-local px) */
interface Fly {
    readonly sprite: Sprite
    readonly shadow: Sprite
    /** when it comes */
    readonly arrives: number
    /** where it is in its wingbeat and zigzag */
    readonly phase: number
    x: number
    y: number
    vx: number
    vy: number
    /** where it's going, and whether to land there */
    tx: number
    ty: number
    landing: boolean
    /** it picks somewhere new to go then (in the air) */
    turnAt: number
    /** it sits until then (0: in the air) */
    sitUntil: number
}

interface Corpse {
    readonly view: Container
    readonly gear: PlayerGear
    readonly left: TrenchioSprite
    readonly right: TrenchioSprite
    /** where the hands were, and where they come to rest */
    readonly from: readonly [Vector, Vector]
    readonly to: readonly [Vector, Vector]
    /** the hands' scale while alive */
    readonly grip: readonly [Vector, Vector]
    readonly start: number
    readonly dark: number
    readonly rotation: number
    /** how far it turns as it falls */
    readonly twist: number
    /** the blood soaking out round its bullet holes, and the size each comes to */
    readonly soaks: Array<{ readonly sprite: Sprite, readonly size: number }>
    readonly flies: Fly[]
    settled: boolean
}

interface Half {
    readonly view: Container
    readonly texture: Texture
    vx: number
    vy: number
    vrot: number
}

/** what's left of one player: a body (or two halves of one), and the pool under it */
interface Remains {
    readonly id: number
    readonly layer: Layer
    readonly x: number
    readonly y: number
    body?: Corpse
    halves?: Half[]
    pool?: Pool
    /** its body gone for too many lying about, its pool lying on (`_dropBody`): the skull shows again (1.06.1) */
    bodyGone?: boolean
    fadeAt: number
    fadeFor: number
}

interface SpriteLook {
    readonly texture: Texture
    readonly x: number
    readonly y: number
    readonly sx: number
    readonly sy: number
    readonly rotation: number
    readonly tint: number
}

/** a player as they were the moment they died */
interface Snapshot {
    readonly id: number
    readonly layer: Layer
    readonly x: number
    readonly y: number
    readonly rotation: number
    readonly scale: number
    readonly body: SpriteLook
    readonly emblem?: SpriteLook
    readonly fists: readonly [SpriteLook, SpriteLook]
    readonly gear: GearLook
    /** the blood soaked into their uniform, carried over to the body */
    readonly blood: Container
    /** where the bullets of their last half a minute went in, the latest last */
    readonly wounds: readonly Wound[]
    readonly disguised: boolean
}

/** where a bullet went in: sprite px in the player's own frame (it turns with them), in from the rim where it struck */
interface Wound {
    readonly at: number
    readonly x: number
    readonly y: number
    readonly damage: number
    /** one of several landing together (a shotgun's pellets): a small hole */
    readonly pellet: boolean
}

interface Pending {
    readonly id: number
    readonly at: number
    readonly snap?: Snapshot
    /** first seen already dead (died out of sight, or before a replay or a switch of view): lies as they fell, no show */
    readonly seenDead: boolean
    kill?: KillData
}

interface HitRecord {
    readonly at: number
    readonly kind: "bullet" | "melee"
    readonly dir: number
    readonly x: number
    readonly y: number
    readonly damage: number
    /** damage within the last moment (a shotgun's pellets add up) */
    readonly burst: number
    readonly weapon?: MeleeDefinition
}

/** blood soaked into a living player's uniform, or a cut: gone after a while, or kept on their body when they die */
interface Spot {
    readonly sprite: Sprite
    readonly born: number
    /** a soak goes from bright to wet red; a cut is drawn in its own colours */
    readonly dries: boolean
    /** on a body now: it goes when the body goes */
    kept: boolean
}

type CauseKind = "gun" | "sharp" | "blunt" | "chainsaw" | "blast" | "gas" | "fire" | "plain" | "quiet";
interface Cause {
    readonly kind: CauseKind
    /** the way the killing blow went (radians) */
    readonly dir: number
    readonly damage: number
    /** which hand the blow was nearest: 1 right, -1 left */
    readonly side: 1 | -1
    /** where the killing bullet came out (world px) */
    readonly exit: { readonly x: number, readonly y: number }
}

const spriteFrom = (look: SpriteLook, sprite: Sprite = new Sprite()): Sprite => {
    sprite.texture = look.texture;
    sprite.anchor.set(0.5);
    sprite.position.set(look.x, look.y);
    sprite.scale.set(look.sx, look.sy);
    sprite.rotation = look.rotation;
    sprite.tint = look.tint;
    return sprite;
};

const lookOf = (sprite: Sprite): SpriteLook => ({
    texture: sprite.texture,
    x: sprite.position.x,
    y: sprite.position.y,
    sx: sprite.scale.x,
    sy: sprite.scale.y,
    rotation: sprite.rotation,
    tint: sprite.tint
});

class GoreManagerClass {
    private readonly _layers = new Map<Layer, Roots>();
    private _stains: Stain[] = [];
    private _drying: Stain[] = [];
    private _stainLive = 0;
    private _drops: Drop[] = [];
    private _mists: Mist[] = [];
    private _parts: Part[] = [];
    private _partLive = 0;
    private _emitters: Emitter[] = [];
    private _spots: Spot[] = [];
    private readonly _remains = new Map<number, Remains>();
    /** players whose remains have come and gone: never laid down again */
    private readonly _gone = new Set<number>();
    private readonly _pending = new Map<number, Pending>();
    private readonly _earlyKills = new Map<number, { readonly at: number, readonly data: KillData }>();
    private readonly _hits = new Map<number, HitRecord>();
    /** where bullets went into each living player lately (their holes, if they die) */
    private readonly _wounds = new Map<number, Wound[]>();
    private readonly _sawAt = new Map<number, number>();
    private _blasts: Array<{ readonly at: number, readonly x: number, readonly y: number, readonly layer: Layer, readonly radius: number }> = [];
    private readonly _dots: Sprite[] = [];
    private _now = 0;
    private _lastSweep = 0;

    get level(): GoreLevel {
        return GameConsole.getBuiltInCVar("cv_gore");
    }

    private get _tier(): Tier {
        const quality = WarLook.quality;
        return quality === "high" ? TIERS.high : quality === "low" ? TIERS.low : TIERS.medium;
    }

    private _levelChanged(level: GoreLevel): void {
        if (level === "off") this._clearAll();
    }

    /** is a player's body lying over their death marker (the skull shows again once it's gone) */
    hidesMarker(playerID: number): boolean {
        return !this._broken && this._lying(playerID);
    }

    /**
     * the remains lie there still: a body, its halves, or what a blast left of it (a pool alone, its body gone for too
     * many lying about, hides nothing: 1.06.1; 1.06.1's second review, a blast's gibs, no body among them, showed the
     * skull and the name over them)
     */
    private _lying(playerID: number): boolean {
        const remains = this._remains.get(playerID);
        return remains !== undefined && remains.bodyGone !== true;
    }

    /** the death marker of a player's death, if this page has it (the server's word on the body's pool and age, 1.06.1) */
    private _markerOf(playerID: number): DeathMarker | undefined {
        for (const marker of Game.objects.getCategory(ObjectCategory.DeathMarker)) {
            if (marker.playerID === playerID) return marker;
        }
        return undefined;
    }

    // --------------------------------------------------------------------------------------------------- hits

    private _hit(player: Player, position: Vector, angle: number, gore?: GoreHit): void {
        this._now = performance.now();
        const level = this.level;
        if (level === "off" || player.bloodless) {
            this._dust(position, angle, player.layer);
            return;
        }
        if (gore?.kind === "melee") {
            this._meleeHit(player, position, angle, gore.weapon, level === "extreme");
            return;
        }

        const extreme = level === "extreme";
        const x = this._xm(extreme);
        const layer = player.layer;
        const cx = player.position.x * PIXI_SCALE, cy = player.position.y * PIXI_SCALE;
        const R = RADIUS * player.sizeMod;
        // a bullet goes on through the body; anything else splashes back the way it came
        const dir = gore?.kind === "bullet" ? Math.atan2(gore.direction.y, gore.direction.x) : angle;
        const damage = gore?.kind === "bullet" ? gore.damage : 12;
        const ix = position.x * PIXI_SCALE, iy = position.y * PIXI_SCALE;
        const repeat = this._record(player.id, "bullet", dir, ix, iy, damage);
        if (gore?.kind === "bullet") this._wound(player, ix - cx, iy - cy, damage, repeat);
        const k = repeat ? 0.45 : 1;
        const wet = this._wetAt(cx, cy, layer);
        const exit = this._exitWound(cx, cy, R, dir, ix, iy);
        const w = powerOf(damage);

        this._spray(layer, exit.x, exit.y, dir, 0.8, w.n * x.n * k, 100 * M, w.sMax * x.s * M, 1.4 * M, w.rMax * x.r * M, wet);
        if (!repeat || Math.random() < 0.35) this._mist(layer, exit.x, exit.y, 5 * M, w.mist * x.mist * M, 450, extreme ? 0.55 : 0.45);
        if (!repeat) this._mist(layer, ix, iy, 3 * M, 9 * M, 220, 0.35);
        if (!wet && (!repeat || Math.random() < 0.5)) {
            this._stain(layer, exit.x + Math.cos(dir) * 7 * M, exit.y + Math.sin(dir) * 7 * M, w.splat * x.splat * M, dir, 1.3, "splat");
        }
        this._soak(player);

        if (!extreme || repeat) return;
        // Extreme: big hits throw flesh, and the biggest bone too
        const skin = this._skinColor(player.images.body.tint, player.gear.look);
        if (damage >= 45 || Math.random() < clamp01((damage - 10) / 30) * 0.6) {
            this._chunk(layer, exit.x, exit.y, skin, dir + (Math.random() - 0.5) * 0.4, between(210, 260) * M, between(150, 175) * M, 4.5 * M, 1.6 * M);
        }
        if (damage >= 45) {
            for (let i = 0; i < 2; i++) this._bone(layer, exit.x, exit.y, dir + (Math.random() - 0.5) * 0.9, between(170, 320) * M, between(110, 200) * M, between(4, 6.5) * M);
        }
    }

    private _bleed(player: Player): void {
        this._now = performance.now();
        if (this.level === "off" || player.bloodless) return;
        const layer = player.layer;
        const x = player.position.x * PIXI_SCALE + (Math.random() - 0.5) * 30, y = player.position.y * PIXI_SCALE + (Math.random() - 0.5) * 30;
        const wet = this._wetAt(x, y, layer);
        this._spray(layer, x, y, Math.random() * TAU, TAU, 2, 10 * M, 40 * M, 1.4 * M, 2.4 * M, wet);
        if (!wet) this._stain(layer, x, y, between(3, 5) * M, Math.random() * TAU, 1, "splat");
    }

    private _meleeHit(player: Player, position: Vector, dir: number, weapon: MeleeDefinition, extreme: boolean): void {
        const layer = player.layer;
        const cx = player.position.x * PIXI_SCALE, cy = player.position.y * PIXI_SCALE;
        const R = RADIUS * player.sizeMod;
        const kind = meleeKind(weapon);
        this._record(player.id, "melee", dir, position.x * PIXI_SCALE, position.y * PIXI_SCALE, weapon.damage, weapon);
        const wet = this._wetAt(cx, cy, layer);
        const more = extreme ? 1 : 0.6;

        if (kind === "chainsaw") {
            // the chainsaw bites many times a second: blood flies off the chain to either side, a few times a second
            const last = this._sawAt.get(player.id) ?? 0;
            if (this._now - last < 40) return;
            this._sawAt.set(player.id, this._now);
            const sx = cx - Math.cos(dir) * R * 0.75, sy = cy - Math.sin(dir) * R * 0.75;
            const side = sign();
            this._spray(layer, sx, sy, dir + side * (Math.PI / 2 - 0.35), 0.7, 2 * (extreme ? 1 : 0.75), 110 * M, 300 * M, 1.3 * M, 3 * M, wet);
            if (Math.random() < 0.25) this._spray(layer, sx, sy, dir + Math.PI + (Math.random() - 0.5) * 0.8, 0.5, 1, 60 * M, 160 * M, 1.2 * M, 2.2 * M, wet);
            if (Math.random() < 0.12) this._mist(layer, sx, sy, 4 * M, 22 * M, 350, 0.45);
            if (extreme && Math.random() < 0.05) {
                this._chunk(layer, sx, sy, this._skinColor(player.images.body.tint, player.gear.look), dir + side * 1.2, 180 * M, 140 * M, 3.5 * M, 1.3 * M);
            }
            if (Math.random() < 0.06) this._soak(player);
            return;
        }

        const contactX = cx - Math.cos(dir) * R * 0.6, contactY = cy - Math.sin(dir) * R * 0.6;
        if (kind === "sharp") {
            // the blade sweeps across: blood is flung off along its swing, and a curved streak lands behind
            const side = sign();
            const sweep = dir + side * Math.PI / 2;
            this._spray(layer, contactX, contactY, sweep - side * 0.35, 0.9, 13 * more, 120 * M, 280 * M, 1.6 * M, 3.6 * M, wet);
            this._spray(layer, contactX, contactY, dir, 0.7, 5 * more, 80 * M, 220 * M, 1.5 * M, 3.2 * M, wet);
            this._mist(layer, contactX, contactY, 5 * M, 24 * M, 400, 0.5);
            if (!wet) this._streak(layer, cx, cy, R, dir, side, extreme ? 9 : 6);
            this._soak(player);
            if (extreme) this._cut(player, dir, false);
            return;
        }

        // blunt: a smaller splash where it struck
        this._spray(layer, contactX, contactY, dir, 0.8, 6 * (extreme ? 1.4 : 1), 90 * M, 240 * M, 1.4 * M, 3 * M, wet);
        this._mist(layer, contactX, contactY, 4 * M, 18 * M, 350, 0.4);
        this._soak(player);
    }

    /** Off, and anything that doesn't bleed: a puff of dust */
    private _dust(position: Vector, angle: number, layer: Layer): void {
        const x = position.x * PIXI_SCALE, y = position.y * PIXI_SCALE;
        for (let i = 0; i < 3; i++) {
            const a = angle + (Math.random() - 0.5) * 1.2, v = between(20, 60);
            this._mist(layer, x, y, 8, between(28, 40), between(550, 800), 0.6, DUST_TINT, Math.cos(a) * v, Math.sin(a) * v);
        }
    }

    private _explosion(position: Vector, layer: Layer, radius: number): void {
        this._now = performance.now();
        this._blasts = this._blasts.filter(b => this._now - b.at < 2000);
        this._blasts.push({ at: this._now, x: position.x * PIXI_SCALE, y: position.y * PIXI_SCALE, layer, radius: radius * PIXI_SCALE });
    }

    // --------------------------------------------------------------------------------------------------- deaths

    private _playerDied(player: Player): void {
        this._now = performance.now();
        if (this.level === "off" || player.bloodless) return;
        const early = this._earlyKills.get(player.id);
        this._earlyKills.delete(player.id);
        this._pending.set(player.id, { id: player.id, at: this._now, snap: this._snapshot(player), seenDead: false, kill: early?.data });
    }

    private _playerSeenDead(player: Player): void {
        this._now = performance.now();
        if (this.level === "off" || this._remains.has(player.id) || this._gone.has(player.id) || this._pending.has(player.id)) return;
        this._pending.set(player.id, { id: player.id, at: this._now, seenDead: true });
    }

    private _killInfo(data: KillData): void {
        if (!data.killed) return;
        const pending = this._pending.get(data.victimId);
        if (pending) pending.kill = data;
        else this._earlyKills.set(data.victimId, { at: performance.now(), data });
    }

    private _snapshot(player: Player): Snapshot {
        const { images } = player;
        // the blood on their uniform goes with the body, and so do the bullets' holes
        const blood = new Container();
        for (const child of [...images.blood.children]) blood.addChild(child);
        for (const spot of this._spots) {
            if (spot.sprite.parent === blood) spot.kept = true;
        }
        const wounds = (this._wounds.get(player.id) ?? []).filter(wound => this._now - wound.at < WOUND_TIME);
        this._wounds.delete(player.id);
        return {
            id: player.id,
            layer: player.layer,
            x: player.position.x * PIXI_SCALE,
            y: player.position.y * PIXI_SCALE,
            rotation: player.container.rotation,
            // (their size, not the climb's or the landing's moment's: a body shot on a ladder was drawn small; 1.10.1's audit)
            scale: player.sizeMod,
            body: lookOf(images.body),
            emblem: images.emblem.visible ? lookOf(images.emblem) : undefined,
            fists: [lookOf(images.leftFist), lookOf(images.rightFist)],
            gear: player.gear.look,
            blood,
            wounds,
            disguised: player.disguised
        };
    }

    private _resolve(pending: Pending): void {
        const level = this.level;
        if (level === "off") {
            pending.snap?.blood.destroy({ children: true });
            return;
        }
        let snap = pending.snap;
        if (!snap) {
            const player = Game.objects.get(pending.id);
            if (player?.isPlayer !== true || player.destroyed || !player.dead || player.bloodless) return;
            snap = this._snapshot(player);
        }
        if (snap.disguised) {
            snap.blood.destroy({ children: true });
            return;
        }
        const extreme = level === "extreme";
        const wet = this._wetAt(snap.x, snap.y, snap.layer);
        const R = RADIUS * snap.scale;

        // the server's word on its pool, and when it died (a body first seen dead: 1.06.1's audit, every one was drawn
        // with a pool, a gas death's too, kept four minutes from when it was seen: the pools drawn are those that give
        // prints, and gone as theirs go)
        const marker = this._markerOf(pending.id);
        if (pending.seenDead) {
            const remains = this._addRemains(snap);
            remains.body = this._corpse(snap, true, ASHEN);
            this._holes(remains.body, snap.wounds);
            // (dried as long as it's lain there, by the server's word on when it died: 1.06.1's second review, every one
            // first seen dead was drawn as a minute old)
            if (marker?.pool !== false) remains.pool = this._pool(snap.layer, snap.x, snap.y, R * (extreme ? 2.6 : 2.1), 1, true, wet, marker && Math.min(this._now, marker.diedAt));
            if (marker) remains.fadeAt = Math.min(remains.fadeAt, marker.diedAt + LIFETIME);
            return;
        }

        const cause = this._cause(pending, snap);
        if (extreme && cause.kind === "blast") {
            this._blowApart(snap, cause, wet, marker?.pool !== false);
            return;
        }
        if (extreme && cause.kind === "chainsaw") {
            this._sawApart(snap, cause, wet, marker?.pool !== false);
            return;
        }

        const remains = this._addRemains(snap);
        const body = remains.body = this._corpse(snap, false, cause.kind === "fire" ? BURNT : ASHEN);
        this._holes(body, snap.wounds, cause);
        const x = this._xm(extreme);
        const { dir } = cause;
        const layer = snap.layer;
        const quietDeath = cause.kind === "gas" || cause.kind === "fire" || cause.kind === "quiet";

        if (marker ? marker.pool : !quietDeath) {
            const poolR = extreme ? 2.6 : cause.kind === "sharp" || cause.kind === "chainsaw" ? 2.3 : 2.1;
            remains.pool = this._pool(layer, snap.x, snap.y, R * poolR, 2800, false, wet);
        }

        switch (cause.kind) {
            case "gun": {
                const w = powerOf(cause.damage);
                this._spray(layer, cause.exit.x, cause.exit.y, dir, 0.75, w.n * x.n * 0.8, 110 * M, w.sMax * x.s * (extreme ? 1.6 : 1.3) * M, 1.6 * M, w.rMax * x.r * 1.1 * M, wet);
                this._mist(layer, cause.exit.x, cause.exit.y, 6 * M, w.mist * x.mist * 1.3 * M, 450, extreme ? 0.55 : 0.45);
                // Extreme: the killing shot can take an arm off (the bigger the gun, the likelier)
                if (extreme && Math.random() < clamp01(0.45 + cause.damage / 50)) this._armOff(snap, body, cause, wet);
                break;
            }
            case "sharp": {
                const side = sign(), sweep = dir + side * Math.PI / 2;
                const contactX = snap.x - Math.cos(dir) * R * 0.6, contactY = snap.y - Math.sin(dir) * R * 0.6;
                this._spray(layer, contactX, contactY, sweep - side * 0.35, 0.9, 22 * (extreme ? 1 : 0.6), 120 * M, 360 * M, 1.6 * M, 4.4 * M, wet);
                this._spray(layer, contactX, contactY, dir, 0.7, 10 * (extreme ? 1 : 0.6), 80 * M, 220 * M, 1.5 * M, 3.2 * M, wet);
                this._mist(layer, contactX, contactY, 5 * M, 34 * M, 400, 0.5);
                if (!wet) this._streak(layer, snap.x, snap.y, R, dir, side, extreme ? 9 : 6);
                if (extreme) this._cutBody(body, snap, dir);
                break;
            }
            case "blunt": {
                this._spray(layer, snap.x, snap.y, dir, 0.9, 10 * (extreme ? 1.4 : 1), 90 * M, 260 * M, 1.5 * M, 3.4 * M, wet);
                this._mist(layer, snap.x, snap.y, 5 * M, 24 * M, 400, 0.45);
                break;
            }
            case "chainsaw": {
                const n = dir + Math.PI / 2;
                this._spray(layer, snap.x, snap.y, n, 0.9, 10, 90 * M, 300 * M, 1.6 * M, 4 * M, wet);
                this._spray(layer, snap.x, snap.y, n + Math.PI, 0.9, 10, 90 * M, 300 * M, 1.6 * M, 4 * M, wet);
                this._mist(layer, snap.x, snap.y, 8 * M, 50 * M, 600, 0.5);
                break;
            }
            case "blast": {
                this._spray(layer, snap.x, snap.y, dir, 2.4, 30, 120 * M, 420 * M, 1.6 * M, 4.2 * M, wet);
                this._mist(layer, snap.x, snap.y, 10 * M, 70 * M, 700, 0.55);
                break;
            }
            default:
                // gas, fire, bleeding out, a fall: the body just drops
                break;
        }
    }

    private _cause(pending: Pending, snap: Snapshot): Cause {
        const { kill } = pending;
        const hit = this._hits.get(snap.id);
        const recent = hit !== undefined && this._now - hit.at < 1500 ? hit : undefined;
        const fromHit = (): CauseKind => (recent ? (recent.kind === "bullet" ? "gun" : meleeKind(recent.weapon)) : "plain");

        let kind: CauseKind;
        switch (kill?.damageSource) {
            case DamageSources.Gun: kind = "gun"; break;
            case DamageSources.Melee: kind = meleeKind(kill.weaponUsed ?? recent?.weapon); break;
            case DamageSources.Explosion:
            case DamageSources.Artillery: kind = "blast"; break;
            case DamageSources.Throwable: kind = "blunt"; break;
            // a kill with no weapon to name goes out as gas: a hit right before it tells what it was
            case DamageSources.Gas: kind = recent !== undefined && this._now - recent.at < 600 ? fromHit() : "gas"; break;
            case DamageSources.Fire: kind = "fire"; break;
            case DamageSources.Disconnect: kind = "quiet"; break;
            case DamageSources.BleedOut:
            case DamageSources.FinallyKilled:
            case DamageSources.Obstacle: kind = "plain"; break;
            default: kind = recent ? fromHit() : this._nearestBlast(snap) ? "blast" : "plain";
        }

        let dir = Math.random() * TAU;
        const blast = kind === "blast" ? this._nearestBlast(snap) : undefined;
        if (blast) {
            dir = Math.atan2(snap.y - blast.y, snap.x - blast.x);
        } else if (recent) {
            dir = recent.dir;
        } else if (kill?.attackerId !== undefined && kill.attackerId !== snap.id) {
            const attacker = Game.objects.get(kill.attackerId);
            if (attacker) dir = Math.atan2(snap.y - attacker.position.y * PIXI_SCALE, snap.x - attacker.position.x * PIXI_SCALE);
        }

        let damage = recent?.burst ?? 20;
        if (!recent && kind === "gun" && kill?.weaponUsed) {
            const gun = kill.weaponUsed as { readonly ballistics?: { readonly damage?: number }, readonly bulletCount?: number };
            damage = (gun.ballistics?.damage ?? 20) * (gun.bulletCount ?? 1);
        }

        // which hand the blow was nearest, from where it struck
        let side: 1 | -1 = sign();
        const R = RADIUS * snap.scale;
        let exit = { x: snap.x + Math.cos(dir) * R * 0.9, y: snap.y + Math.sin(dir) * R * 0.9 };
        if (recent && recent.kind === "bullet") {
            const lx = recent.x - snap.x, ly = recent.y - snap.y;
            const local = -lx * Math.sin(snap.rotation) + ly * Math.cos(snap.rotation);
            if (Math.abs(local) > 2) side = local > 0 ? 1 : -1;
            exit = this._exitWound(snap.x, snap.y, R, dir, recent.x, recent.y);
        }
        return { kind, dir, damage, side, exit };
    }

    private _nearestBlast(snap: Snapshot): { readonly x: number, readonly y: number } | undefined {
        let best: { readonly x: number, readonly y: number } | undefined;
        let bestD = Infinity;
        for (const blast of this._blasts) {
            if (this._now - blast.at > 1200 || Math.abs(blast.layer - snap.layer) > 1) continue;
            const d = Math.hypot(blast.x - snap.x, blast.y - snap.y);
            if (d < blast.radius * 1.6 + 2 * PIXI_SCALE && d < bestD) {
                best = blast;
                bestD = d;
            }
        }
        return best;
    }

    private _addRemains(snap: Snapshot): Remains {
        // an id the server gave out again: the old body goes
        const old = this._remains.get(snap.id);
        if (old) {
            this._destroyRemains(old);
            this._remains.delete(snap.id);
        }
        const remains: Remains = { id: snap.id, layer: snap.layer, x: snap.x, y: snap.y, fadeAt: this._now + LIFETIME, fadeFor: FADE };
        this._remains.set(snap.id, remains);
        // the oldest bodies go when there are too many (their pools stay as long as they would have: they give prints,
        // and the server counts them, 1.06.1's audit)
        // (this one counted: its body comes once this returns, and the cap let one more lie about: 1.06.1's second review)
        let over = -this._tier.corpses;
        for (const other of this._remains.values()) if (other.bodyGone !== true && other.fadeAt > this._now) over++;
        for (const other of this._remains.values()) {
            if (over <= 0) break;
            if (other === remains || other.fadeAt <= this._now || other.bodyGone === true) continue;
            this._dropBody(other);
            over--;
        }
        this._syncMarkers(snap.id);
        return remains;
    }

    /** a body gone for too many lying about: its pool, if it has one, lies on to its time (and the skull shows again) */
    private _dropBody(remains: Remains): void {
        if (!remains.pool) {
            this._fadeRemains(remains, QUICK_FADE);
            return;
        }
        remains.bodyGone = true;
        remains.body?.view.destroy({ children: true });
        remains.body = undefined;
        for (const half of remains.halves ?? []) {
            half.view.destroy({ children: true });
            half.texture.destroy(true);
        }
        remains.halves = undefined;
        this._syncMarkers(remains.id);
    }

    private _fadeRemains(remains: Remains, duration: number): void {
        if (remains.fadeAt <= this._now) return;
        remains.fadeAt = this._now;
        remains.fadeFor = duration;
    }

    /**
     * The body, lying where it fell, so it can't be taken for a player standing still: over a moment it falls limp
     * (see LIMP_OUT), turns a little and drops flat, and the colour drains out of it; flies come a few seconds later
     * (`settled`: already lying, its flies there).
     */
    private _corpse(snap: Snapshot, settled: boolean, dark: number): Corpse {
        const roots = this._rootsFor(snap.layer);
        const view = new Container();
        view.position.set(snap.x, snap.y);
        view.rotation = snap.rotation;
        view.scale.set(snap.scale);
        view.addChild(spriteFrom(snap.body));
        if (snap.emblem) view.addChild(spriteFrom(snap.emblem));
        const left = spriteFrom(snap.fists[0], new TrenchioSprite()) as TrenchioSprite;
        const right = spriteFrom(snap.fists[1], new TrenchioSprite()) as TrenchioSprite;
        left.zIndex = right.zIndex = 1;
        view.addChild(left, right);
        snap.blood.zIndex = 0.5;
        view.addChild(snap.blood);
        const gear = new PlayerGear(view, left, right);
        gear.wear(snap.gear);

        // one arm fallen out to the side (an arm's length, swung a little one way or the other), the other across the body
        const out = LIMP_ANGLE + between(-0.3, 0.3);
        const flung = Vec(LIMP_SHOULDER.x + Math.cos(out) * LIMP_REACH, LIMP_SHOULDER.y + Math.sin(out) * LIMP_REACH);
        const across = Vec(LIMP_IN.x + between(-4, 4), LIMP_IN.y + between(-4, 4));
        const mirrored = Math.random() < 0.5;
        const corpse: Corpse = {
            view, gear, left, right,
            from: [Vec(left.x, left.y), Vec(right.x, right.y)],
            to: mirrored ? [Vec(across.x, -across.y), Vec(flung.x, -flung.y)] : [flung, across],
            grip: [Vec(left.scale.x, left.scale.y), Vec(right.scale.x, right.scale.y)],
            start: this._now,
            dark,
            rotation: snap.rotation,
            twist: (mirrored ? -1 : 1) * between(0.2, 0.45),
            soaks: [],
            flies: this._swarm(view, settled),
            settled
        };
        this._pose(corpse, settled ? 1 : 0);
        if (settled) view.tint = dark;
        roots.ground.addChild(view);
        return corpse;
    }

    /** a body `k` of the way to lying limp: its hands fall loose, it turns a little, and it drops flat (its shadow goes) */
    private _pose(body: Corpse, k: number): void {
        const [l0, r0] = body.from, [l1, r1] = body.to, [lg, rg] = body.grip;
        const hand = 1 + (LIMP_HAND - 1) * k;
        body.left.position.set(l0.x + (l1.x - l0.x) * k, l0.y + (l1.y - l0.y) * k);
        body.right.position.set(r0.x + (r1.x - r0.x) * k, r0.y + (r1.y - r0.y) * k);
        body.left.scale.set(lg.x * hand, lg.y * hand);
        body.right.scale.set(rg.x * hand, rg.y * hand);
        body.view.rotation = body.rotation + body.twist * k;
        body.gear.update({
            position: Vec(body.view.x / PIXI_SCALE, body.view.y / PIXI_SCALE),
            rotation: body.view.rotation,
            visible: true,
            inWater: false,
            leftFist: body.left,
            rightFist: body.right,
            shadow: 1 - k
        });
    }

    /**
     * Bullet holes where the latest shots went in (pellets landing together make small ones), the blood soaking out
     * round each; shot dead without our seeing the shots land: one where the killing shot went in, facing the shooter.
     */
    private _holes(body: Corpse, wounds: readonly Wound[], cause?: Cause): void {
        let list = wounds.slice(-MAX_HOLES);
        if (list.length === 0 && cause?.kind === "gun") {
            const a = cause.dir + Math.PI - body.rotation, d = RADIUS * between(0.45, 0.7);
            list = [{ at: this._now, x: Math.cos(a) * d, y: Math.sin(a) * d, damage: Math.min(cause.damage, 60), pellet: false }];
        }
        const set = bodyShapes();
        const placed: Wound[] = [];
        for (const wound of list) {
            // one right on top of another is the same hole
            if (placed.some(other => Math.hypot(other.x - wound.x, other.y - wound.y) < 6)) continue;
            placed.push(wound);
            // bigger for a bigger gun
            const size = wound.pellet ? between(0.65, 0.8) : Math.min(1.7, Math.max(1, 0.9 + wound.damage / 50));
            const soak = new Sprite(pick(set.soaks));
            soak.anchor.set(SOAK_CX / SOAK_W, 0.5);
            soak.position.set(wound.x, wound.y);
            // it runs off toward the ground: out to the side the body lies on it
            soak.rotation = Math.atan2(wound.y, wound.x) + (Math.random() - 0.5) * 0.9;
            soak.zIndex = 0.52;
            const full = size * between(0.95, 1.15);
            soak.scale.set(body.settled ? full : full * 0.5);
            const hole = new Sprite(pick(set.holes));
            hole.anchor.set(0.5);
            hole.position.set(wound.x, wound.y);
            hole.rotation = Math.random() * TAU;
            hole.scale.set(size);
            hole.zIndex = 0.54;
            body.view.addChild(soak, hole);
            body.soaks.push({ sprite: soak, size: full });
        }
    }

    /** the flies that come to a body: the first a few seconds after, the rest over the next half a minute or so */
    private _swarm(view: Container, settled: boolean): Fly[] {
        const flies: Fly[] = [];
        const set = bodyShapes();
        let arrives = this._now + (settled ? 0 : between(2500, 5000));
        for (let i = 0; i < this._tier.flies; i++) {
            const shadow = new Sprite(shapes().dot);
            shadow.anchor.set(0.5);
            shadow.tint = SHADOW_TINT;
            shadow.alpha = 0.3;
            shadow.scale.set((3.3 * FLY_SCALE) / DOT_R, (2 * FLY_SCALE) / DOT_R);
            shadow.zIndex = 1.9;
            shadow.visible = false;
            const sprite = new Sprite(set.flying[0]);
            sprite.anchor.set(0.5);
            sprite.scale.set(FLY_SCALE);
            sprite.zIndex = 2;
            sprite.visible = false;
            view.addChild(shadow, sprite);
            // from out of sight, or (already there) round the body
            const a = Math.random() * TAU, d = settled ? between(15, 80) : between(170, 230);
            flies.push({
                sprite, shadow, arrives, phase: Math.random() * 1000,
                x: Math.cos(a) * d, y: Math.sin(a) * d, vx: 0, vy: 0, tx: 0, ty: 0, landing: false, turnAt: 0, sitUntil: 0
            });
            if (!settled) arrives += between(2000, 6000);
        }
        return flies;
    }

    /** Extreme: the killing shot tears an arm off at the shoulder; the stump spurts a few times */
    private _armOff(snap: Snapshot, body: Corpse, cause: Cause, wet: boolean): void {
        const { side, dir } = cause;
        const layer = snap.layer;
        const R = RADIUS * snap.scale;
        const hand = side > 0 ? body.right : body.left;
        hand.visible = false; // the sleeve goes with it (the gear draws none to a hidden hand)
        this._pose(body, body.settled ? 1 : 0);

        const local = side * 1.05;
        const stump = new Sprite(shapes().stump);
        stump.anchor.set(0.5);
        stump.position.set(Math.cos(local) * 42, Math.sin(local) * 42);
        stump.rotation = local;
        stump.zIndex = 0.7;
        body.view.addChild(stump);

        // the arm, from the shoulder, thrown on the way the bullet went
        const shoulder = snap.rotation + side * 1.1;
        const sx = snap.x + Math.cos(shoulder) * R, sy = snap.y + Math.sin(shoulder) * R;
        const fly = dir - side * 0.3;
        this._arm(layer, sx, sy, snap, side > 0 ? 1 : 0, fly, 175 * M, 210 * M, side * 10);

        const view = body.view;
        this._emitters.push({
            pulses: 5, next: this._now + 50, every: 280, n: 5, sMin: 90 * M, sMax: 210 * M, layer, wet,
            origin: () => {
                if (view.destroyed) return undefined;
                const a = view.rotation + local;
                return { x: view.x + Math.cos(a) * 42 * view.scale.x, y: view.y + Math.sin(a) * 42 * view.scale.x, ang: a };
            }
        });

        for (let i = 0; i < 3; i++) {
            this._bone(layer, cause.exit.x, cause.exit.y, dir + (Math.random() - 0.5) * 1.2, between(150, 310) * M, between(120, 220) * M, between(4.5, 7.5) * M);
        }
        this._chunk(layer, cause.exit.x, cause.exit.y, this._skinColor(snap.body.tint, snap.gear), dir + 0.3, 240 * M, 200 * M, 6 * M, 1.8 * M);
        if (!wet) this._stain(layer, snap.x + Math.cos(dir) * 26 * M, snap.y + Math.sin(dir) * 26 * M, 10 * M, dir, 1.6, "splat");
    }

    /** Extreme: an explosion blows the body apart, away from the blast */
    private _blowApart(snap: Snapshot, cause: Cause, wet: boolean, pooled = true): void {
        const layer = snap.layer;
        const away = cause.dir;
        const R = RADIUS * snap.scale;
        const { x, y } = snap;
        const remains = this._addRemains(snap);
        snap.blood.destroy({ children: true });

        this._spray(layer, x, y, away, 3.4, 70, 120 * M, 520 * M, 1.6 * M, 4.6 * M, wet);
        this._spray(layer, x, y, away, 1.2, 24, 260 * M, 640 * M, 2 * M, 5 * M, wet);
        this._mist(layer, x, y, 10 * M, 95 * M, 800, 0.6);
        this._mist(layer, x + Math.cos(away) * 30 * M, y + Math.sin(away) * 30 * M, 8 * M, 70 * M, 600, 0.5);
        if (pooled) remains.pool = this._pool(layer, x + Math.cos(away) * 10 * M, y + Math.sin(away) * 10 * M, R * 1.64, 2200, false, wet);
        if (!wet) {
            for (let i = 0; i < 4; i++) this._stain(layer, x + (Math.random() - 0.5) * 40 * M, y + (Math.random() - 0.5) * 40 * M, between(7, 13) * M, Math.random() * TAU, 1, "splat");
        }

        const spread = (s: number): number => away + (Math.random() - 0.5) * s;
        const at = (): readonly [number, number] => [x + (Math.random() - 0.5) * 12 * M, y + (Math.random() - 0.5) * 12 * M];
        const skin = this._skinColor(snap.body.tint, snap.gear);
        for (const hand of [0, 1] as const) {
            const [px, py] = at();
            this._arm(layer, px, py, snap, hand, spread(2.4), 175 * between(0.7, 1.3) * M, between(180, 400) * M, (Math.random() - 0.5) * 22);
        }
        for (let i = 0; i < 7; i++) {
            const [px, py] = at();
            this._chunk(layer, px, py, skin, spread(3), 165 * between(0.7, 1.3) * M, between(180, 400) * M, between(6, 13) * M, 2.2 * M);
        }
        for (let i = 0; i < 8; i++) {
            const [px, py] = at();
            this._bone(layer, px, py, spread(3.4), 220 * between(0.7, 1.3) * M, between(180, 400) * M, between(4.5, 8.5) * M);
        }
    }

    /** Extreme: the chainsaw cuts the body in two along the saw's line, and the halves fall apart */
    private _sawApart(snap: Snapshot, cause: Cause, wet: boolean, pooled = true): void {
        const layer = snap.layer;
        const { dir } = cause;
        const R = RADIUS * snap.scale;
        const { x, y } = snap;
        const remains = this._addRemains(snap);
        const roots = this._rootsFor(layer);
        const n = dir + Math.PI / 2;

        remains.halves = ([1, -1] as const).map(side => {
            const texture = this._halfTexture(snap, side, dir);
            const view = new Container();
            view.position.set(x, y);
            view.rotation = dir;
            view.scale.set(snap.scale);
            const halo = new Sprite(shapes().mist);
            halo.anchor.set(0.5);
            halo.tint = SHADOW_TINT;
            halo.alpha = 0.45;
            halo.scale.set((RADIUS * 1.15) / MIST_R);
            halo.position.set(0, side * 14);
            const half = new Sprite(texture);
            half.anchor.set(0.5);
            const fist = spriteFrom({ ...snap.fists[side > 0 ? 1 : 0], x: 30, y: side * 58 });
            view.addChild(halo, half, fist);
            view.tint = ASHEN;
            roots.ground.addChild(view);
            return {
                view, texture,
                vx: Math.cos(n) * side * 95 * M + Math.cos(dir) * 25 * M,
                vy: Math.sin(n) * side * 95 * M + Math.sin(dir) * 25 * M,
                vrot: side * 0.9
            };
        });
        snap.blood.destroy({ children: true });

        this._spray(layer, x, y, n, 0.9, 16, 90 * M, 300 * M, 1.6 * M, 4 * M, wet);
        this._spray(layer, x, y, n + Math.PI, 0.9, 16, 90 * M, 300 * M, 1.6 * M, 4 * M, wet);
        this._spray(layer, x, y, dir, 0.6, 10, 80 * M, 240 * M, 1.6 * M, 3.6 * M, wet);
        this._mist(layer, x, y, 8 * M, 60 * M, 600, 0.55);
        if (pooled) remains.pool = this._pool(layer, x, y, R * 2.3, 2400, false, wet);
        const skin = this._skinColor(snap.body.tint, snap.gear);
        for (let i = 0; i < 4; i++) {
            this._chunk(layer, x + (Math.random() - 0.5) * 10 * M, y + (Math.random() - 0.5) * 10 * M, skin, dir, between(40, 120) * M, 80 * M, between(4, 7.5) * M, 1.8 * M);
        }
    }

    /** one half of a body cut along the x axis (side 1: the +y half), with the cut face showing: drawn once into a texture */
    private _halfTexture(snap: Snapshot, side: 1 | -1, cut: number): Texture {
        const temp = new Container();
        const inner = new Container();
        inner.rotation = snap.rotation - cut;
        inner.addChild(spriteFrom(snap.body));
        if (snap.emblem) inner.addChild(spriteFrom(snap.emblem));
        temp.addChild(inner);
        const face = new Graphics();
        face.rect(-RADIUS, side > 0 ? 0 : -7, RADIUS * 2, 7).fill(MEAT);
        for (let i = -3; i <= 3; i++) face.ellipse(i * 9, side * 3.5, 3, 1.6).fill(MEAT_HI);
        face.circle(-4, side * 3, 4).fill(BONE).circle(14, side * 3, 2.6).fill(BONE);
        temp.addChild(face);
        const start = side > 0 ? 0 : Math.PI;
        const mask = new Graphics().moveTo(0, 0).arc(0, 0, RADIUS, start, start + Math.PI).closePath().fill(0xffffff);
        temp.addChild(mask);
        temp.mask = mask;
        const texture = Game.pixi.renderer.generateTexture({
            target: temp,
            frame: new Rectangle(-RADIUS - 2, -RADIUS - 2, RADIUS * 2 + 4, RADIUS * 2 + 4),
            antialias: true
        });
        temp.destroy({ children: true });
        return texture;
    }

    /** a forearm in the player's sleeve, with their hand (`hand`: 0 left, 1 right) */
    private _arm(layer: Layer, x: number, y: number, snap: Snapshot, hand: 0 | 1, angle: number, speed: number, vz: number, vrot: number): void {
        const view = new Container();
        const sleeve = new Sprite(armTexture(snap.gear));
        sleeve.anchor.set(ARM_CX / ARM_W, 0.5);
        const fist = spriteFrom({ ...snap.fists[hand], x: 22, y: 0, rotation: 0 });
        view.addChild(sleeve, fist);
        this._addPart(layer, view, x, y, {
            vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, vz,
            rot: angle, vrot, friction: 4.2, bleed: 2.6 * M, scale: snap.scale, shadowW: 36, shadowH: 14, lifetime: LIFETIME
        });
    }

    private _bone(layer: Layer, x: number, y: number, angle: number, speed: number, vz: number, length: number): void {
        const sprite = new Sprite(shapes().bone);
        sprite.anchor.set(0.5);
        const view = new Container();
        view.addChild(sprite);
        this._addPart(layer, view, x, y, {
            vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, vz,
            rot: Math.random() * TAU, vrot: 14 * sign(), friction: 7, bleed: 0, scale: length / BONE_LEN, shadowW: BONE_LEN, shadowH: BONE_LEN * 0.4, lifetime: LIFETIME
        });
    }

    private _chunk(layer: Layer, x: number, y: number, color: number, angle: number, speed: number, vz: number, size: number, bleed: number): void {
        const sprite = new Sprite(pick(chunkTexturesFor(color)));
        sprite.anchor.set(0.5);
        const view = new Container();
        view.addChild(sprite);
        this._addPart(layer, view, x, y, {
            vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed + (Math.random() - 0.5) * 60 * M, vz,
            rot: Math.random() * TAU, vrot: 9 * sign(), friction: 5, bleed, scale: size / CHUNK_SIZE, shadowW: CHUNK_SIZE, shadowH: CHUNK_SIZE * 0.8, lifetime: LIFETIME
        });
    }

    private _addPart(
        layer: Layer, view: Container, x: number, y: number,
        o: {
            readonly vx: number, readonly vy: number, readonly vz: number, readonly rot: number, readonly vrot: number,
            readonly friction: number, readonly bleed: number, readonly scale: number,
            /** the shadow's half-width and half-height in the part's own (unscaled) units */
            readonly shadowW: number, readonly shadowH: number, readonly lifetime: number
        }
    ): void {
        const roots = this._rootsFor(layer);
        const shadow = this._dot();
        shadow.tint = SHADOW_TINT;
        shadow.alpha = 0.25;
        roots.ground.addChild(shadow);
        view.position.set(x, y);
        view.rotation = o.rot;
        view.scale.set(o.scale);
        roots.air.addChild(view);
        const part: Part = {
            view, shadow, layer, scale: o.scale, friction: o.friction, bleed: o.bleed, shadowW: o.shadowW, shadowH: o.shadowH,
            x, y, z: 6 * M, vx: o.vx, vy: o.vy, vz: o.vz, rot: o.rot, vrot: o.vrot, trail: 0,
            grounded: false, resting: false, fadeAt: this._now + o.lifetime, fadeFor: FADE, fading: false, dead: false
        };
        this._placePart(part);
        this._parts.push(part);
        this._partLive++;
        // the oldest go first when there are too many
        let over = this._partLive - this._tier.parts;
        for (const part of this._parts) {
            if (over <= 0) break;
            if (part.fading || part.dead) continue;
            part.fadeAt = this._now;
            part.fadeFor = QUICK_FADE;
            part.fading = true;
            this._partLive--;
            over--;
        }
    }

    /** blood soaks into the uniform where it shows (round the rim, clear of the helmet) */
    private _soak(player: Player): void {
        if (player.skinHidesBlood) return;
        const blood = player.images.blood;
        if (blood.children.length >= MAX_SPOTS) blood.children[0].destroy();
        const rb = between(2.6, 5.2) * M;
        const a = Math.random() * TAU;
        const d = Math.min(RADIUS * between(0.6, 0.88), RADIUS - 1.5 - rb * 1.72);
        const sprite = new Sprite(pick(shapes().spots));
        sprite.anchor.set(0.5);
        sprite.position.set(Math.cos(a) * d, Math.sin(a) * d);
        sprite.rotation = Math.random() * TAU;
        sprite.scale.set(rb / SPOT_R);
        sprite.tint = FRESH_TINT;
        sprite.alpha = 0.9;
        blood.addChild(sprite);
        this._spots.push({ sprite, born: this._now, dries: true, kept: false });
    }

    /** a blade's cut across a living body (Extreme) */
    private _cut(player: Player, dir: number, deep: boolean): void {
        if (player.skinHidesBlood) return;
        const blood = player.images.blood;
        if (blood.children.length >= MAX_SPOTS) blood.children[0].destroy();
        const sprite = new Sprite(deep ? shapes().deepSlash : pick(shapes().slashes));
        sprite.anchor.set(0.5);
        sprite.rotation = dir - player.container.rotation + Math.PI / 2 + (Math.random() - 0.5) * 0.8;
        if (Math.random() < 0.5) sprite.scale.y = -1;
        blood.addChild(sprite);
        this._spots.push({ sprite, born: this._now, dries: false, kept: false });
    }

    /** the killing blow's cut, on the body */
    private _cutBody(body: Corpse, snap: Snapshot, dir: number): void {
        const sprite = new Sprite(shapes().deepSlash);
        sprite.anchor.set(0.5);
        sprite.rotation = dir - snap.rotation + Math.PI / 2 + (Math.random() - 0.5) * 0.6;
        sprite.zIndex = 0.6;
        body.view.addChild(sprite);
    }

    /** blood flung off a blade lands in a curve behind the one it hit */
    private _streak(layer: Layer, x: number, y: number, R: number, dir: number, side: 1 | -1, count: number): void {
        for (let i = 0; i < count; i++) {
            const a = dir + side * (-0.2 + i * 0.16), d = R * 1.5 + i * 2.5 * M;
            this._stain(layer, x + Math.cos(a) * d, y + Math.sin(a) * d, (4.2 - i * 0.3) * M, a + side * Math.PI / 2, 2.2, "drop");
        }
    }

    // --------------------------------------------------------------------------------------------------- primitives

    private _xm(extreme: boolean): typeof EXTREME | typeof NORMAL {
        return extreme ? EXTREME : NORMAL;
    }

    private _skinColor(bodyTint: number, look: GearLook): number {
        return bodyTint !== 0xffffff ? bodyTint : look.sleeve;
    }

    /** where a bullet comes out: through the body, on its line */
    private _exitWound(cx: number, cy: number, R: number, dir: number, ix: number, iy: number): { readonly x: number, readonly y: number } {
        const nx = -Math.sin(dir), ny = Math.cos(dir);
        const off = Math.max(-R * 0.85, Math.min(R * 0.85, (ix - cx) * nx + (iy - cy) * ny));
        return { x: cx + Math.cos(dir) * R * 0.9 + nx * off, y: cy + Math.sin(dir) * R * 0.9 + ny * off };
    }

    /** remembers the latest hit on a player (for the killing blow); true when it lands right on top of the last one (pellets) */
    private _record(id: number, kind: "bullet" | "melee", dir: number, x: number, y: number, damage: number, weapon?: MeleeDefinition): boolean {
        const previous = this._hits.get(id);
        const repeat = previous !== undefined && previous.kind === kind && this._now - previous.at < 60;
        const burst = previous !== undefined && this._now - previous.at < 150 ? previous.burst + damage : damage;
        this._hits.set(id, { at: this._now, kind, dir, x, y, damage, burst, weapon });
        return repeat;
    }

    /**
     * keeps where a bullet went into a living player, for their body's holes: (dx, dy) from their middle to where it
     * struck (world px), taken into their own frame and in from the rim, so the hole shows on the side it came from
     */
    private _wound(player: Player, dx: number, dy: number, damage: number, pellet: boolean): void {
        if (player.skinHidesBlood) return;
        const a = Math.atan2(dy, dx) - player.container.rotation + (Math.random() - 0.5) * 0.3;
        const d = RADIUS * between(0.42, 0.76);
        let list = this._wounds.get(player.id);
        if (!list) this._wounds.set(player.id, list = []);
        list.push({ at: this._now, x: Math.cos(a) * d, y: Math.sin(a) * d, damage, pellet });
        if (list.length > MAX_HOLES * 3) list.shift();
    }

    /** blood that lands in water just clouds it (no stains) */
    private _wetAt(x: number, y: number, layer: Layer): boolean {
        const terrain = MapManager.terrain as typeof MapManager.terrain | undefined;
        if (!terrain) return false;
        try {
            return terrain.getFloor(Vec(x / PIXI_SCALE, y / PIXI_SCALE), layer) === FloorNames.Water;
        } catch {
            return false;
        }
    }

    private _rootsFor(layer: Layer): Roots {
        let roots = this._layers.get(layer);
        if (!roots) {
            const make = (zIndex: number): Container => {
                const container = new Container();
                container.zIndex = zIndex;
                return container;
            };
            roots = {
                layer,
                pools: make(ZIndexes.Decals + 0.2),
                stains: make(ZIndexes.Decals + 0.4),
                ground: make(ZIndexes.DeathMarkers + 0.5),
                air: make(ZIndexes.Players + 0.6),
                chunks: new Map()
            };
            this._layers.set(layer, roots);
            this._attach(roots);
        }
        return roots;
    }

    /** each layer's gore goes in the container the camera shows that layer in (which changes as you go up or down stairs) */
    private _attach(roots: Roots): void {
        const target = CameraManager.getContainer(roots.layer);
        if (roots.pools.parent === target) return;
        target.addChild(roots.pools, roots.stains, roots.ground, roots.air);
    }

    private _dot(): Sprite {
        const sprite = this._dots.pop() ?? new Sprite(shapes().dot);
        sprite.anchor.set(0.5);
        sprite.rotation = 0;
        sprite.visible = true;
        return sprite;
    }

    private _recycle(sprite: Sprite): void {
        sprite.removeFromParent();
        if (this._dots.length < 600) this._dots.push(sprite);
        else sprite.destroy();
    }

    /** drops thrown into the air at `ang` (± spread/2): each lands as a stain stretched the way it flew */
    private _spray(layer: Layer, x: number, y: number, ang: number, spread: number, n: number, sMin: number, sMax: number, rMin: number, rMax: number, wet: boolean): void {
        const count = Math.round(n * this._tier.drops);
        if (count <= 0) return;
        const roots = this._rootsFor(layer);
        for (let i = 0; i < count && this._drops.length < MAX_DROPS; i++) {
            const a = ang + (Math.random() - 0.5) * spread, s = between(sMin, sMax);
            const sprite = this._dot();
            sprite.tint = FRESH_TINT;
            sprite.alpha = 1;
            const shadow = this._dot();
            shadow.tint = SHADOW_TINT;
            shadow.alpha = 0.22;
            roots.ground.addChild(shadow);
            roots.air.addChild(sprite);
            const drop: Drop = {
                x, y, z: between(4, 10) * M, vx: Math.cos(a) * s, vy: Math.sin(a) * s, vz: between(40, 210) * M,
                rad: between(rMin, rMax), layer, wet, sprite, shadow
            };
            // placed right away: a sprite from the pool still has its last size and place until it is
            this._placeDrop(drop);
            this._drops.push(drop);
        }
    }

    private _placeDrop(d: Drop): void {
        d.sprite.position.set(d.x, d.y);
        d.sprite.scale.set((d.rad * (1 + d.z / (140 * M))) / DOT_R);
        d.shadow.position.set(d.x + d.z * 0.12, d.y + d.z * 0.2);
        d.shadow.scale.set(d.rad / DOT_R);
    }

    private _mist(layer: Layer, x: number, y: number, r0: number, r1: number, life: number, alpha: number, tint = MIST_TINT, vx = 0, vy = 0): void {
        const sprite = new Sprite(shapes().mist);
        sprite.anchor.set(0.5);
        sprite.tint = tint;
        sprite.alpha = 0;
        this._rootsFor(layer).air.addChild(sprite);
        this._mists.push({ sprite, born: this._now, life, r0, r1, alpha, vx, vy, x, y });
    }

    /** a stain on the ground: a drop (stretched the way it flew), a splat, or a smear */
    private _stain(layer: Layer, x: number, y: number, rad: number, ang: number, stretch: number, kind: "drop" | "splat" | "smear"): void {
        const set = shapes();
        let sprite: Sprite;
        if (kind === "splat") {
            sprite = new Sprite(pick(set.splats));
            sprite.anchor.set(0.5);
            sprite.scale.set(rad / SPLAT_R);
            sprite.rotation = Math.random() * TAU;
        } else {
            let bucket = 0;
            for (const [i, s] of DROP_STRETCH.entries()) {
                if (Math.abs(s - stretch) < Math.abs(DROP_STRETCH[bucket] - stretch)) bucket = i;
            }
            const variants = set.drops[bucket];
            sprite = new Sprite(kind === "smear" || Math.random() < 0.4 ? variants[0] : variants[Math.random() < 0.5 ? 1 : 2]);
            sprite.anchor.set(DROP_CX / DROP_W, 0.5);
            sprite.scale.set((rad * stretch) / (DROP_R * DROP_STRETCH[bucket]), rad / DROP_R);
            sprite.rotation = ang;
        }
        const alpha = kind === "smear" ? 0.62 : 0.92;
        sprite.alpha = alpha;
        sprite.tint = FRESH_TINT;
        sprite.position.set(x, y);
        const roots = this._rootsFor(layer);
        const key = chunkKey(x, y);
        let chunk = roots.chunks.get(key);
        if (!chunk) {
            chunk = new Container();
            roots.chunks.set(key, chunk);
            roots.stains.addChild(chunk);
        }
        chunk.addChild(sprite);

        const stain: Stain = { sprite, born: this._now, alpha, fadeAt: this._now + LIFETIME, fadeFor: FADE, step: 0, fading: false, dead: false };
        this._stains.push(stain);
        this._drying.push(stain);
        this._stainLive++;
        if (this._stainLive > this._tier.stains) {
            // the oldest go (a few at a time, so it isn't every new drop)
            let over = this._stainLive - this._tier.stains + 8;
            for (const old of this._stains) {
                if (over <= 0) break;
                if (old.fading || old.dead) continue;
                old.fading = true;
                old.fadeAt = this._now;
                old.fadeFor = QUICK_FADE;
                this._stainLive--;
                over--;
            }
        }
    }

    /** a pool that spreads out under a body (`settled`: already spread) */
    private _pool(layer: Layer, x: number, y: number, maxR: number, grow: number, settled: boolean, wet: boolean, bornAt?: number): Pool | undefined {
        if (wet) {
            this._mist(layer, x, y, maxR * 0.3, maxR * 1.1, 6000, 0.3);
            return undefined;
        }
        const set = shapes();
        const sprite = new Sprite(pick(set.pools));
        sprite.anchor.set(0.5);
        sprite.position.set(x, y);
        sprite.rotation = Math.random() * TAU;
        sprite.alpha = 0.95;
        sprite.scale.set(0);
        const shine = new Sprite(set.dot);
        shine.anchor.set(0.5);
        shine.tint = SHINE_TINT;
        shine.alpha = 0.1;
        shine.rotation = -0.5;
        shine.scale.set(0);
        this._rootsFor(layer).pools.addChild(sprite, shine);
        return { sprite, shine, born: bornAt ?? (settled ? this._now - 60_000 : this._now), grow, maxR, x, y, step: -1 };
    }

    // --------------------------------------------------------------------------------------------------- every frame

    private _update(deltaMS: number): void {
        const now = this._now = performance.now();
        const dt = Math.min(deltaMS, 50) / 1000;

        for (const roots of this._layers.values()) this._attach(roots);

        for (const pending of this._pending.values()) {
            if (pending.kill === undefined && now - pending.at < KILL_WAIT) continue;
            this._pending.delete(pending.id);
            this._resolve(pending);
        }

        if (now - this._lastSweep > 2000) {
            this._lastSweep = now;
            for (const [id, hit] of this._hits) if (now - hit.at > 3000) this._hits.delete(id);
            for (const [id, wounds] of this._wounds) if (now - (wounds[wounds.length - 1]?.at ?? 0) > WOUND_TIME) this._wounds.delete(id);
            for (const [id, at] of this._sawAt) if (now - at > 3000) this._sawAt.delete(id);
            for (const [id, early] of this._earlyKills) if (now - early.at > 3000) this._earlyKills.delete(id);
        }

        this._stepDrops(dt);
        this._stepMists(dt);
        this._stepEmitters();
        this._stepParts(dt);
        this._stepRemains(dt);
        this._stepStains();
        this._stepSpots();
        this._cull();
    }

    private _stepDrops(dt: number): void {
        let w = 0;
        const drag = Math.max(0, 1 - 0.8 * dt);
        for (const d of this._drops) {
            d.x += d.vx * dt;
            d.y += d.vy * dt;
            d.z += d.vz * dt;
            d.vz -= GRAVITY * dt;
            d.vx *= drag;
            d.vy *= drag;
            if (d.z <= 0) {
                if (!d.wet) this._stain(d.layer, d.x, d.y, d.rad * 1.25, Math.atan2(d.vy, d.vx), 1 + Math.min(2.2, Math.hypot(d.vx, d.vy) / (160 * M)), "drop");
                this._recycle(d.sprite);
                this._recycle(d.shadow);
                continue;
            }
            this._placeDrop(d);
            this._drops[w++] = d;
        }
        this._drops.length = w;
    }

    private _stepMists(dt: number): void {
        let w = 0;
        for (const m of this._mists) {
            const k = (this._now - m.born) / m.life;
            if (k >= 1) {
                m.sprite.destroy();
                continue;
            }
            m.x += m.vx * dt;
            m.y += m.vy * dt;
            const r = m.r0 + (m.r1 - m.r0) * (1 - (1 - k) ** 2);
            m.sprite.position.set(m.x, m.y);
            m.sprite.scale.set(r / MIST_R);
            m.sprite.alpha = m.alpha * (1 - k);
            this._mists[w++] = m;
        }
        this._mists.length = w;
    }

    private _stepEmitters(): void {
        let w = 0;
        for (const e of this._emitters) {
            if (this._now >= e.next) {
                e.next = this._now + e.every;
                e.pulses--;
                const origin = e.origin();
                if (origin) this._spray(e.layer, origin.x, origin.y, origin.ang + (Math.random() - 0.5) * 0.2, 0.35, e.n, e.sMin, e.sMax, 1.4 * M, 2.8 * M, e.wet);
                else e.pulses = 0;
            }
            if (e.pulses > 0) this._emitters[w++] = e;
        }
        this._emitters.length = w;
    }

    private _stepParts(dt: number): void {
        let w = 0;
        for (const p of this._parts) {
            if (p.fadeAt <= this._now) {
                if (!p.fading) {
                    p.fading = true;
                    this._partLive--;
                }
                const k = (this._now - p.fadeAt) / p.fadeFor;
                if (k >= 1) {
                    p.dead = true;
                    p.view.destroy({ children: true });
                    this._recycle(p.shadow);
                    continue;
                }
                p.view.alpha = 1 - k;
                p.shadow.visible = false;
            }
            if (!p.resting) this._movePart(p, dt);
            this._parts[w++] = p;
        }
        this._parts.length = w;
    }

    private _movePart(p: Part, dt: number): void {
        const ox = p.x, oy = p.y;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += p.vrot * dt;
        if (p.z > 0 || p.vz > 0) {
            p.z += p.vz * dt;
            p.vz -= GRAVITY * dt;
            if (p.bleed > 0 && Math.random() < dt * 14) this._spray(p.layer, p.x, p.y, Math.random() * TAU, TAU, 1, 10 * M, 40 * M, 1.2 * M, 2.2 * M, false);
            if (p.z <= 0) {
                p.z = 0;
                p.vz = Math.abs(p.vz) > 160 * M ? -p.vz * 0.28 : 0;
                if (p.bleed > 0 && !this._wetAt(p.x, p.y, p.layer)) this._stain(p.layer, p.x, p.y, p.bleed * 1.5, Math.random() * TAU, 1, "splat");
            }
        } else {
            const k = Math.max(0, 1 - p.friction * dt);
            p.vx *= k;
            p.vy *= k;
            p.vrot *= k;
            const speed = Math.hypot(p.vx, p.vy);
            p.trail += Math.hypot(p.x - ox, p.y - oy);
            if (p.bleed > 0 && speed > 30 * M && p.trail > 4 * M) {
                p.trail = 0;
                this._stain(p.layer, p.x, p.y, p.bleed * 0.85, Math.atan2(p.vy, p.vx), 1.8, "smear");
            }
            if (!p.grounded) {
                // down on the ground now: under players and loot
                p.grounded = true;
                this._rootsFor(p.layer).ground.addChild(p.view);
            }
            if (speed < 1 && Math.abs(p.vrot) < 0.02) p.resting = true;
        }
        this._placePart(p);
    }

    private _placePart(p: Part): void {
        const lift = 1 + p.z / (160 * M);
        p.view.position.set(p.x, p.y);
        p.view.rotation = p.rot;
        p.view.scale.set(p.scale * lift);
        const flying = p.z > 0.5;
        p.shadow.visible = flying && !p.fading;
        if (flying) {
            p.shadow.position.set(p.x + p.z * 0.1, p.y + p.z * 0.18);
            p.shadow.rotation = p.rot;
            p.shadow.scale.set((p.shadowW * p.scale) / DOT_R, (p.shadowH * p.scale) / DOT_R);
        }
    }

    private _stepRemains(dt: number): void {
        for (const remains of this._remains.values()) {
            const body = remains.body;
            if (body) {
                if (!body.settled) this._settle(body);
                // flies: only near the camera (a body out of view is hidden, see _cull)
                if (body.flies.length > 0 && body.view.visible) this._stepFlies(body, dt);
            }
            if (remains.halves) {
                for (const half of remains.halves) {
                    if (Math.abs(half.vx) + Math.abs(half.vy) + Math.abs(half.vrot) < 0.5) continue;
                    half.view.x += half.vx * dt;
                    half.view.y += half.vy * dt;
                    half.view.rotation += half.vrot * dt;
                    const k = Math.max(0, 1 - 3.2 * dt);
                    half.vx *= k;
                    half.vy *= k;
                    half.vrot *= k;
                }
            }
            const pool = remains.pool;
            if (pool) {
                const age = (this._now - pool.born) / 1000;
                const k = 1 - (1 - clamp01((age * 1000) / pool.grow)) ** 3;
                if (k < 1 || pool.step < 0) {
                    pool.sprite.scale.set((k * pool.maxR) / POOL_R);
                    pool.shine.position.set(pool.x - 0.22 * pool.maxR * k, pool.y - 0.28 * pool.maxR * k);
                    pool.shine.scale.set((0.28 * pool.maxR * k) / DOT_R, (0.1 * pool.maxR * k) / DOT_R);
                }
                // a pool dries far slower than a splash
                const poolAge = 0.9 + age * 0.15;
                const step = dryStep(poolAge);
                if (step !== pool.step) {
                    pool.step = step;
                    pool.sprite.tint = bloodAt(poolAge);
                }
            }
            if (remains.fadeAt <= this._now) {
                const k = (this._now - remains.fadeAt) / remains.fadeFor;
                if (k >= 1) {
                    this._destroyRemains(remains);
                    this._remains.delete(remains.id);
                    this._gone.add(remains.id);
                    this._syncMarkers(remains.id);
                    continue;
                }
                const alpha = 1 - k;
                if (body) body.view.alpha = alpha;
                for (const half of remains.halves ?? []) half.view.alpha = alpha;
                if (pool) {
                    pool.sprite.alpha = 0.95 * alpha;
                    pool.shine.alpha = 0.1 * alpha;
                }
            }
        }
    }

    /** a body falling limp (easing to rest), then the colour draining out of it as the blood soaks out round its holes */
    private _settle(body: Corpse): void {
        const age = this._now - body.start;
        this._pose(body, 1 - (1 - clamp01(age / SETTLE)) ** 2);
        const t = clamp01(age / DRAIN);
        body.view.tint = mix(WHITE, toRGB(body.dark), t);
        const soaked = 0.5 + 0.5 * (1 - (1 - t) ** 2);
        for (const soak of body.soaks) soak.sprite.scale.set(soak.size * soaked);
        if (t >= 1) body.settled = true;
    }

    /** flies dart about over a body, zigzagging, and now and then land on it (or on the blood by it) for a few seconds */
    private _stepFlies(body: Corpse, dt: number): void {
        const now = this._now;
        const set = bodyShapes();
        // a fly's shadow falls down and to the right on screen, whichever way the body lies
        const c = Math.cos(-body.view.rotation), s = Math.sin(-body.view.rotation);
        for (const fly of body.flies) {
            if (now < fly.arrives) continue;
            const { sprite, shadow } = fly;
            if (!sprite.visible) {
                sprite.visible = true;
                sprite.alpha = 0;
                this._flyOn(fly, false);
            }
            if (sprite.alpha < 1) sprite.alpha = Math.min(1, sprite.alpha + dt * 2.5);

            if (fly.sitUntil > now) {
                // sitting: now and then it turns, or takes a step
                if (Math.random() < dt * 1.5) {
                    sprite.rotation += (Math.random() - 0.5) * 1.8;
                    fly.x += Math.cos(sprite.rotation) * 1.5;
                    fly.y += Math.sin(sprite.rotation) * 1.5;
                    sprite.position.set(fly.x, fly.y);
                }
                continue;
            }
            if (fly.sitUntil !== 0) {
                fly.sitUntil = 0;
                this._flyOn(fly, true);
            }

            let dx = fly.tx - fly.x, dy = fly.ty - fly.y;
            if (Math.hypot(dx, dy) < 3) {
                if (fly.landing) {
                    fly.sitUntil = now + between(800, 3200);
                    fly.vx = fly.vy = 0;
                    sprite.texture = set.sitting;
                    sprite.rotation = Math.random() * TAU;
                    shadow.visible = false;
                    continue;
                }
                this._flyOn(fly, false);
            } else if (!fly.landing && now > fly.turnAt) {
                this._flyOn(fly, false);
            }
            dx = fly.tx - fly.x;
            dy = fly.ty - fly.y;
            const dist = Math.hypot(dx, dy) || 1;
            // it slows to land
            const speed = fly.landing ? Math.min(170, 40 + dist * 3) : 170;
            const k = Math.min(1, dt * 7);
            fly.vx += ((dx / dist) * speed - fly.vx) * k;
            fly.vy += ((dy / dist) * speed - fly.vy) * k;
            const zigzag = Math.sin(now * 0.021 + fly.phase) * (fly.landing ? 20 : 60);
            const vx = fly.vx - (dy / dist) * zigzag, vy = fly.vy + (dx / dist) * zigzag;
            fly.x += vx * dt;
            fly.y += vy * dt;
            sprite.position.set(fly.x, fly.y);
            sprite.rotation = Math.atan2(vy, vx);
            // its wings beat (two drawings, swapped every 30 ms: a blur)
            sprite.texture = set.flying[Math.floor(now / 30 + fly.phase) & 1];
            shadow.visible = true;
            shadow.alpha = 0.3 * sprite.alpha;
            shadow.position.set(fly.x + 3.5 * c - 6 * s, fly.y + 3.5 * s + 6 * c);
            shadow.rotation = sprite.rotation;
        }
    }

    /** where a fly goes next: somewhere round the body, or now and then down onto it, or the blood by it (not straight after taking off) */
    private _flyOn(fly: Fly, takingOff: boolean): void {
        const land = !takingOff && Math.random() < 0.16;
        const a = Math.random() * TAU;
        const d = land ? (Math.random() < 0.75 ? between(0, 34) : between(48, 66)) : between(20, 95);
        fly.tx = Math.cos(a) * d;
        fly.ty = Math.sin(a) * d;
        fly.landing = land;
        fly.turnAt = this._now + between(250, 900);
    }

    private _destroyRemains(remains: Remains): void {
        remains.body?.view.destroy({ children: true });
        for (const half of remains.halves ?? []) {
            half.view.destroy({ children: true });
            half.texture.destroy(true);
        }
        remains.pool?.sprite.destroy();
        remains.pool?.shine.destroy();
    }

    private _stepStains(): void {
        const now = this._now;
        // drying: only the young ones change colour
        let w = 0;
        for (const s of this._drying) {
            if (s.dead) continue;
            const age = (now - s.born) / 1000;
            const step = dryStep(age);
            if (step !== s.step) {
                s.step = step;
                s.sprite.tint = bloodAt(age);
            }
            if (age < DRY_SECONDS + 3) this._drying[w++] = s;
        }
        this._drying.length = w;

        // fading: the oldest are at the front
        let dead = 0;
        let leading = true;
        for (const s of this._stains) {
            if (s.fadeAt > now) break;
            if (!s.dead) {
                if (!s.fading) {
                    s.fading = true;
                    this._stainLive--;
                }
                const k = (now - s.fadeAt) / s.fadeFor;
                if (k >= 1) {
                    s.dead = true;
                    s.sprite.destroy();
                } else {
                    s.sprite.alpha = s.alpha * (1 - k);
                }
            }
            if (s.dead && leading) dead++;
            else leading = false;
        }
        if (dead) this._stains.splice(0, dead);
    }

    private _stepSpots(): void {
        let w = 0;
        for (const spot of this._spots) {
            if (spot.sprite.destroyed) continue;
            const age = this._now - spot.born;
            if (age < 2000) {
                if (spot.dries) spot.sprite.tint = mix(FRESH, WET, Math.floor(age / 250) / 8);
            } else if (spot.kept) continue; // on a body now, and dried: the body takes it from here
            if (!spot.kept && age > SPOT_LIFETIME) {
                const k = (age - SPOT_LIFETIME) / 1000;
                if (k >= 1) {
                    spot.sprite.destroy();
                    continue;
                }
                spot.sprite.alpha = 0.9 * (1 - k);
            }
            this._spots[w++] = spot;
        }
        this._spots.length = w;
    }

    /** only what's near the camera is drawn */
    private _cull(): void {
        const scale = CameraManager.container.scale.x || 1;
        const margin = 320;
        const x0 = -CameraManager.container.position.x / scale - margin, y0 = -CameraManager.container.position.y / scale - margin;
        const x1 = x0 + CameraManager.width / scale + margin * 2, y1 = y0 + CameraManager.height / scale + margin * 2;
        const near = (x: number, y: number): boolean => x > x0 && x < x1 && y > y0 && y < y1;

        for (const roots of this._layers.values()) {
            for (const [key, chunk] of roots.chunks) {
                const cx = Math.floor(key / 4096) * CHUNK, cy = (key % 4096) * CHUNK;
                chunk.visible = cx + CHUNK > x0 && cx < x1 && cy + CHUNK > y0 && cy < y1;
            }
        }
        for (const remains of this._remains.values()) {
            const visible = near(remains.x, remains.y);
            if (remains.body) remains.body.view.visible = visible;
            for (const half of remains.halves ?? []) half.view.visible = visible;
            if (remains.pool) remains.pool.sprite.visible = remains.pool.shine.visible = visible;
        }
        for (const part of this._parts) {
            if (part.resting) part.view.visible = near(part.x, part.y);
        }
    }

    // --------------------------------------------------------------------------------------------------- clean up

    /** show or hide the skull of a player whose body lies over it */
    private _syncMarkers(playerID: number): void {
        const hidden = this._lying(playerID);
        for (const marker of Game.objects.getCategory(ObjectCategory.DeathMarker)) {
            if (marker.playerID === playerID) marker.container.visible = !hidden;
        }
    }

    private _clearAll(): void {
        for (const stain of this._stains) {
            if (!stain.dead) stain.sprite.destroy();
        }
        this._stains = [];
        this._drying = [];
        this._stainLive = 0;
        for (const drop of this._drops) {
            drop.sprite.destroy();
            drop.shadow.destroy();
        }
        this._drops = [];
        for (const mist of this._mists) mist.sprite.destroy();
        this._mists = [];
        for (const part of this._parts) {
            if (part.dead) continue;
            part.view.destroy({ children: true });
            part.shadow.destroy();
        }
        this._parts = [];
        this._partLive = 0;
        this._emitters = [];
        for (const spot of this._spots) {
            if (!spot.sprite.destroyed) spot.sprite.destroy();
        }
        this._spots = [];
        const ids = [...this._remains.keys()];
        for (const remains of this._remains.values()) this._destroyRemains(remains);
        this._remains.clear();
        for (const id of ids) this._syncMarkers(id);
        for (const pending of this._pending.values()) pending.snap?.blood.destroy({ children: true });
        this._pending.clear();
        this._wounds.clear();
        for (const roots of this._layers.values()) {
            for (const chunk of roots.chunks.values()) chunk.destroy({ children: true });
            roots.chunks.clear();
        }
        for (const sprite of this._dots) sprite.destroy();
        this._dots.length = 0;
    }

    private _reset(): void {
        this._clearAll();
        for (const roots of this._layers.values()) {
            for (const container of [roots.pools, roots.stains, roots.ground, roots.air]) container.destroy({ children: true });
        }
        this._layers.clear();
        this._gone.clear();
        this._earlyKills.clear();
        this._hits.clear();
        this._sawAt.clear();
        this._blasts = [];
    }

    // --------------------------------------------------------------------------------------------------- the game's calls
    // Blood is decoration: a fault in it must never cost a frame or a packet. Every call from the game is guarded; the
    // first error is logged, everything drawn is taken away, and blood stays off for the rest of the visit.

    private _broken = false;

    private _safely(task: () => void): void {
        if (this._broken) return;
        try {
            task();
        } catch (error) {
            this._broken = true;
            console.error("Blood & gore stopped after an error (the game carries on without it):", error);
            try {
                this._clearAll();
            } catch {
                // what's left stays where it is
            }
        }
    }

    /** something hit a living player */
    hit(player: Player, position: Vector, angle: number, gore?: GoreHit): void { this._safely(() => this._hit(player, position, angle, gore)); }
    /** a downed player bleeding where they lie */
    bleed(player: Player): void { this._safely(() => this._bleed(player)); }
    /** an explosion went off (bodies near it come apart away from it) */
    explosion(position: Vector, layer: Layer, radius: number): void { this._safely(() => this._explosion(position, layer, radius)); }
    /** a player we were watching has just died: remember them as they were, before their gear drops */
    playerDied(player: Player): void { this._safely(() => this._playerDied(player)); }
    /** a player first seen already dead: their body lies where they fell (once their skin is on, next frame) */
    playerSeenDead(player: Player): void { this._safely(() => this._playerSeenDead(player)); }
    /** how someone died (the kill packet comes in the same message as the death) */
    killInfo(data: KillData): void { this._safely(() => this._killInfo(data)); }
    /** every frame */
    update(deltaMS: number): void { this._safely(() => this._update(deltaMS)); }
    /** the setting changed: Off takes every drop of blood away at once */
    levelChanged(level: GoreLevel): void { this._safely(() => this._levelChanged(level)); }
    /** take every drop of blood away */
    clear(): void { this._safely(() => this._clearAll()); }
    /** a new world (a new match, the death replay, or a switch of whose view is watched) */
    reset(): void { this._safely(() => this._reset()); }
}

export const GoreManager = new GoreManagerClass();
